// React page for managing trays (seeding and transplanting)
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useDrag, useDrop } from 'react-dnd';
import useClientSettings from '../lib/settings';
import ReactDOM from 'react-dom';
import {
  isSplitSlot, slotTrays, isSlotOccupied, halfLabel,
  isBenchEmpty, findFirstEmptyCell, daysSince, formatBenchName,
  resolveHarvestInfo, calcBoxReadiness, buildHarvestForecast,
} from '../lib/trayUtils';
import { stagger } from '../lib/stagger';
import { ModalShell, PageHeader } from '../lib/ui';
import {
  positionId, buildStepStates, benchesForMove, describeStep,
} from '../lib/arrangementSteps';

// ---------- API base ----------
const RAW_BASE =
  process.env.REACT_APP_BASE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');
const BASE = String(RAW_BASE).replace(/\/$/, '');

const axiosClient = axios.create({
  baseURL: BASE,
  headers: { 'Content-Type': 'application/json' },
});
axiosClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
axiosClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) window.location.href = '/login';
    return Promise.reject(err);
  }
);

// ---------- helpers (pure logic lives in trayUtils.js so it can be tested) ----------
const ITEM_TYPE = 'TRAY';

// ---------- Stage pill ----------
function StagePill({ stage }) {
  const map = {
    starter:     { label: 'Starter',     cls: 'stage-pill stage-pill--starter' },
    transplanted:{ label: 'Transplanted',cls: 'stage-pill stage-pill--transplanted' },
    direct:      { label: 'Direct',      cls: 'stage-pill stage-pill--direct' },
  };
  const s = (stage || 'direct').toLowerCase();
  const { label, cls } = map[s] || map.direct;
  return <span className={cls}>{label}</span>;
}

// ---------- Tray ----------
function Tray({
  benchName, layerIndex, trayIndex, trayData,
  mode, selectedStarter, transplantTargets,
  handleTrayClick, onMove,
  halfIndex = null,
}) {
  const isHalf = halfIndex != null;
  const [hover, setHover] = useState(false);
  const trayRef = useRef(null);
  const showTimer = useRef(null);
  const hideTimer = useRef(null);
  const portalRef = useRef(null);
  const [cardSize, setCardSize] = useState({ w: 0, h: 0 });
  const [cardPos, setCardPos] = useState({ top: 0, left: 0, side: 'top' });

  useEffect(() => () => {
    clearTimeout(showTimer.current);
    clearTimeout(hideTimer.current);
  }, []);

  const placeHoverCard = useCallback((size = cardSize) => {
    const r = trayRef.current?.getBoundingClientRect();
    if (!r) return;
    const gap = 10;
    const vw = window.innerWidth, vh = window.innerHeight;
    const w = size.w || 680, h = size.h || 160;
    const spaceBelow = vh - r.bottom;
    const side = spaceBelow >= r.top ? 'bottom' : 'top';
    const cx = r.left + r.width / 2;
    let top = side === 'top' ? r.top - gap - h : r.bottom + gap;
    let left = cx - w / 2;
    const m = 8;
    setCardPos({
      top: Math.max(m, Math.min(top, vh - h - m)),
      left: Math.max(m, Math.min(left, vw - w - m)),
      side,
    });
  }, [cardSize]);

  useEffect(() => {
    if (!hover) return;
    const measure = () => {
      if (portalRef.current) {
        const r = portalRef.current.getBoundingClientRect();
        const size = { w: r.width, h: r.height };
        setCardSize(size);
        placeHoverCard(size);
      } else placeHoverCard();
    };
    measure();
    const on = () => requestAnimationFrame(measure);
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
    };
  }, [hover, placeHoverCard]);

  // FIX: Detect if this is a Germination Chamber tray
  const isChamber = benchName === 'germinationChamber' || trayData?.bench === 'germinationChamber';

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: () => {
      // Return the correct data structure depending on where it's coming from
      if (isChamber) {
        return {
          bench: 'germinationChamber',
          trayId: trayData.id || trayData._id,
          size: trayData?.size,
        };
      }
      return {
        bench: benchName, layer: layerIndex, tray: trayIndex,
        ...(isHalf ? { half: halfIndex } : {}),
        size: trayData?.size,
      };
    },
    // Trays are only draggable if they have a crop and we aren't in a specific mode
    canDrag: () => mode === 'idle' && !!trayData?.cropType,
    collect: (m) => ({ isDragging: m.isDragging() }),
  });

const [{ isOver, canDrop }, drop] = useDrop({
  accept: ITEM_TYPE,
  canDrop: (item) => {
    if (mode !== 'idle') return false;
    // Chamber cells append - nothing gets displaced, any size is fine
    if (isChamber) return true;
    const itemIsHalf = item?.size === 'half';
    // A full tray can never land on a half cell
    if (isHalf && !itemIsHalf) return false;
    // Half trays don't displace anything: only empty halves / empty cells
    if (itemIsHalf && trayData?.cropType) return false;
    return true;
  },
  drop: (item) => {
    if (mode !== 'idle') return;
    const to = {
      bench: benchName, layer: layerIndex, tray: trayIndex,
      ...(isHalf ? { half: halfIndex } : {}),
    };
    if (item.bench === 'germinationChamber') {
      onMove({ from: { bench: 'germinationChamber', trayId: item.trayId }, to });
    } else {
      if (
        item.bench === to.bench && item.layer === to.layer && item.tray === to.tray &&
        (item.half ?? null) === (isHalf ? halfIndex : null)
      ) return;
      onMove({ from: item, to });
    }
  },
  collect: (m) => ({ isOver: m.isOver(), canDrop: m.canDrop() }),  // ← add this
});

  const isSelectedStarter =
    selectedStarter?.bench === benchName &&
    selectedStarter?.layer === layerIndex &&
    selectedStarter?.tray === trayIndex &&
    (selectedStarter?.half ?? null) === (isHalf ? halfIndex : null);
  const isTarget = transplantTargets.some(
    (t) => t.bench === benchName && t.layer === layerIndex && t.tray === trayIndex &&
      (t.half ?? null) === (isHalf ? halfIndex : null)
  );

  const stage = (trayData?.stage || 'direct').toLowerCase();
  const ageDays = trayData?.seededAt ? daysSince(trayData.seededAt) ?? 0 : 0;

  const PALETTE = useMemo(() => ({
    empty:       '#b0bec5',
    selected:    '#f59e0b',
    target:      '#34d399',
    starter:     (d) => `hsl(196,75%,${56 - Math.min(d, 30) / 30 * 16}%)`,
    direct:      (d) => `hsl(40,88%,${52 - Math.min(d, 40) / 40 * 10}%)`,
    transplanted:(d) => `hsl(148,52%,${50 - Math.min(d, 60) / 60 * 12}%)`,
  }), []);

  let bgColor;
  if (isSelectedStarter) bgColor = PALETTE.selected;
  else if (isTarget)     bgColor = PALETTE.target;
  else if (!trayData?.cropType) bgColor = PALETTE.empty;
  else if (stage === 'starter')      bgColor = PALETTE.starter(ageDays);
  else if (stage === 'transplanted') bgColor = PALETTE.transplanted(ageDays);
  else bgColor = PALETTE.direct(ageDays);

  const td = trayData || {};
  const label   = td.cropType || 'Empty';
  const cellLabel = `L${layerIndex + 1} T${trayIndex + 1}${isHalf ? `·${halfLabel(halfIndex)}` : ''}`;
  const loc     = isChamber
    ? 'Germination Chamber'
    : `L${layerIndex + 1}·T${trayIndex + 1}${isHalf ? halfLabel(halfIndex) : ''} @ ${benchName}${isHalf ? ' · ½ tray' : ''}`;
  const seeded  = td.seededAt ? `Seeded ${td.seededAt}` : null;
  const transpl = td.transplantedAt ? `Transplanted ${new Date(td.transplantedAt).toLocaleDateString()}` : null;
  const notes   = (td.notes || '').trim();
  const chamberLoc = isChamber ? (td.chamberLocation || '').trim() : null;
  const origin  = td.origin ? `From ${td.origin.bench} L${td.origin.layer+1} T${td.origin.tray+1}` : null;
  const batch   = td.batchId ? `Batch: ${td.batchId}` : null;
  const seedAmt = td.seedUnit === 'seeds'
    ? (td.seedCount != null ? `${td.seedCount} seeds` : null)
    : (td.seedWeight != null ? `${td.seedWeight}g seed` : null);
  const legacy  = Array.isArray(td.legacyNotes) ? td.legacyNotes : [];
  const stageColor = stage === 'starter' ? '#06b6d4' : stage === 'transplanted' ? '#10b981' : '#f59e0b';
  
  // FIX: Force cursor hand (grab) when draggable
  const isDraggable = !!td.cropType && mode === 'idle';

  return (
    <div
      ref={(node) => { trayRef.current = node; drag(drop(node)); }}
      role="button" tabIndex={0}
      className={isHalf ? 'tray-cell tray-cell--half' : 'tray-cell'}
      aria-label={`${label}. ${loc}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleTrayClick({ bench: benchName, layer: layerIndex, tray: trayIndex, ...(isHalf ? { half: halfIndex } : {}) });
        }
      }}
      onClick={() => handleTrayClick({ bench: benchName, layer: layerIndex, tray: trayIndex, ...(isHalf ? { half: halfIndex } : {}) })}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px) scale(1.04)';
        e.currentTarget.style.boxShadow = 'var(--shadow)';
        e.currentTarget.style.zIndex = 4000;
        clearTimeout(hideTimer.current);
        showTimer.current = setTimeout(() => { setHover(true); placeHoverCard(); }, 80);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = 'var(--shadow-card)';
        e.currentTarget.style.zIndex = 'auto';
        clearTimeout(showTimer.current);
        hideTimer.current = setTimeout(() => setHover(false), 60);
      }}
      style={{
        textAlign: 'center', position: 'relative',
        backgroundColor: isDragging ? '#78909c' : bgColor,
        opacity: isDragging ? 0.55 : 1,
        border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 'var(--r-md)',
        cursor: isDraggable ? 'grab' : 'pointer', // Show the drag hand
        color: '#fff', transition: 'transform .15s ease, box-shadow .15s ease',
        boxShadow: 'var(--shadow-card)',
        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
        ...stagger(0), // removed gridStagger for simplicity in chamber
          outline: isOver && canDrop
    ? (trayData?.cropType ? '2px dashed #f59e0b' : '2px dashed #34d399')
    : 'none',
  outlineOffset: 2,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.85, marginBottom: 3 }}>
        {isChamber ? 'GERM' : cellLabel}{!isChamber && isHalf ? ' ½' : ''}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
      {td.cropType && (
        <div style={{ fontSize: 10, marginTop: 4, opacity: 0.75 }}>{ageDays}d</div>
      )}

      {hover && !isDragging && trayData && ReactDOM.createPortal(
        <div ref={portalRef} style={{ position: 'fixed', top: cardPos.top, left: cardPos.left, zIndex: 100000, pointerEvents: 'none' }}>
          <div style={{
            pointerEvents: 'auto',
            background: 'var(--panel, #fff)',
            color: 'var(--text, #111a14)',
            border: '1px solid var(--border, #d4e0d4)',
            borderRadius: 'var(--r-2xl)',
            boxShadow: 'var(--shadow-pop)',
            padding: 16,
            width: 'min(64vw, 680px)',
            animation: `${cardPos.side === 'top' ? 'td-fade-down' : 'td-fade-up'} 130ms ease-out`,
          }}>
            <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:8 }}>
              <div style={{ fontWeight:800, fontSize:17 }}>{td.cropType}</div>
              <div style={{ fontSize:12, opacity:.6 }}>{loc}</div>
              <StagePill stage={stage} />
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
              {[td.size === 'half' ? '½ size tray' : null, seeded, transpl, origin, batch, seedAmt, `${ageDays}d old`, chamberLoc ? `📍 ${chamberLoc}` : null, td.source === 'ai' ? 'Added by AI (undo on the AI activity page)' : null].filter(Boolean).map((t, i) => (
                <span key={i} style={{
                  padding:'3px 9px', borderRadius:999,
                  background:`${stageColor}1a`, border:`1px solid ${stageColor}55`,
                  color: stageColor, fontSize:12, fontWeight:700,
                  ...stagger(i, 'td-fade-up', 20, 30, 250),
                }}>{t}</span>
              ))}
            </div>
            {notes && (
              <div style={{
                background:'var(--panel-2, #eef2ee)',
                border:'1px solid var(--border, #d4e0d4)',
                borderRadius:'var(--r-lg)', padding:'9px 12px',
              }}>
                <div style={{ fontWeight:700, fontSize:12, marginBottom:3, opacity:.7 }}>Notes</div>
                <div style={{ whiteSpace:'pre-wrap', fontSize:13 }}>{notes}</div>
              </div>
            )}
            {!!legacy.length && (
              <div style={{ marginTop:8, fontSize:12, opacity:.65 }}>
                <strong>{legacy.length} legacy note{legacy.length > 1 ? 's' : ''}</strong>
                {' - '}{legacy[legacy.length-1].text.split('\n')[0].slice(0,80)}…
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ---------- Collapsed bench ----------
function CollapsedBenchCard({ benchName, bench, onSeedFirst, onExpand, onMove, mode }) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ITEM_TYPE,
    canDrop: () => mode === 'idle',
    drop: (item) => {
      if (mode !== 'idle') return;
      const spot = findFirstEmptyCell(bench);
      onExpand();
      if (!spot) return;

      const to = { bench: benchName, layer: spot.layer, tray: spot.tray };
      
      if (item.bench === 'germinationChamber') {
        onMove({
          from: { bench: 'germinationChamber', trayId: item.trayId },
          to,
        });
      } else {
        onMove({ from: item, to });
      }
    },
    collect: (m) => ({ isOver: m.isOver(), canDrop: m.canDrop() }),
  });

  return (
    <div
      ref={drop}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onExpand(); }}
      style={{
        background: 'var(--panel, #fff)',
        borderRadius: 'var(--r-xl)',
        padding: 20,
        boxShadow: 'var(--shadow-card)',
        border: '1px solid var(--border, #d4e0d4)',
        outline: isOver && canDrop ? '2px dashed #34d399' : 'none',
        outlineOffset: 4,
        cursor: 'pointer',
        transition: 'box-shadow .2s',
      }}
      onClick={() => {
        const spot = findFirstEmptyCell(bench);
        if (!spot) return onExpand();
        onSeedFirst(benchName, spot.layer, spot.tray);
      }}
    >
      <h3 style={{ fontSize: 22, marginBottom: 14, textAlign: 'center', fontWeight: 700, color: 'var(--text, #111a14)' }}>
        {formatBenchName(benchName)}
      </h3>
      <div style={{
        display: 'grid', placeItems: 'center', padding: 18,
        border: '1.5px dashed var(--border, #d4e0d4)', borderRadius: 'var(--r-lg)',
        background: 'var(--panel-2, #eef2ee)',
      }}>
        <div style={{ textAlign: 'center', color: 'var(--muted, #5e7265)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, opacity: .7 }}>Empty bench</div>
          <button
            onClick={(e) => { e.stopPropagation(); onExpand(); }}
            style={{
              background: 'var(--panel, #fff)', color: 'var(--text, #111a14)',
              border: '1px solid var(--border, #d4e0d4)',
              padding: '6px 12px', borderRadius: 'var(--r-md)', fontWeight: 700, fontSize: 13,
            }}
          >Show cells</button>
        </div>
      </div>
    </div>
  );
}


// resolveHarvestInfo and calcBoxReadiness are imported from '../lib/trayUtils'

// ── Readiness badge (used in the All-trays list) ─────────────────────────────
function ReadinessBadge({ ageDays, cropName, seedInventory }) {
  const { type, targetDays } = resolveHarvestInfo(cropName, seedInventory);
 
  if (type === 'repeat') {
    return (
      <div style={{ minWidth: 110 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#10b981', marginBottom: 2 }}>✂ Repeat</div>
        <div style={{ fontSize: 10, color: 'var(--muted, #5e7265)' }}>Cut &amp; come again</div>
      </div>
    );
  }
 
  const pct      = Math.min(100, Math.round((ageDays / targetDays) * 100));
  const daysLeft = Math.max(0, targetDays - ageDays);
  const overdue  = ageDays > targetDays;
  const barColor = overdue ? '#b3ef44' : pct >= 100 ? '#10b981' : pct >= 75 ? '#f59e0b' : '#3b82f6';
  const label    = overdue
    ? `⚠ ${ageDays - targetDays}d Over`
    : pct >= 100 ? '✓ Ready to harvest'
    : daysLeft === 1 ? '1 day left' : `${daysLeft}d left`;
  const labelColor = overdue ? '#b3ef44' : pct >= 100 ? '#10b981' : pct >= 75 ? '#f59e0b' : 'var(--muted, #5e7265)';
 
  return (
    <div style={{ minWidth: 110 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: labelColor }}>{label}</span>
        <span style={{ fontSize: 10, color: 'var(--muted, #5e7265)' }}>{pct}%</span>
      </div>
      <div style={{ height: 5, borderRadius: 999, background: 'var(--panel-2, #eef2ee)', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 999, background: barColor, width: `${pct}%`, transition: 'width .5s ease' }} />
      </div>
      <div style={{ fontSize: 10, color: 'var(--muted, #5e7265)', marginTop: 2 }}>
        {targetDays}d target · {ageDays}d grown
      </div>
    </div>
  );
}

// ── Grouped Ready view ───────────────────────────────────────────────────────
function ReadyGroupedView({ rows, onTrayClick }) {
  const [openGroups, setOpenGroups] = useState({});
  const toggleGroup = name => setOpenGroups(prev => ({ ...prev, [name]: !prev[name] }));

  const groups = useMemo(() => {
    const map = {};
    rows.forEach(row => {
      const k = row.tray.cropType;
      if (!map[k]) map[k] = [];
      map[k].push(row);
    });
    return Object.entries(map).sort(([, a], [, b]) => {
      // Repeat crops first, then overdue (oldest first), then ready
      const aRepeat = a[0].harvestType === 'repeat';
      const bRepeat = b[0].harvestType === 'repeat';
      if (aRepeat && !bRepeat) return -1;
      if (bRepeat && !aRepeat) return 1;
      return Math.max(...b.map(r => r.ageDays)) - Math.max(...a.map(r => r.ageDays));
    });
  }, [rows]);

  const stageColors = { starter: '#06b6d4', direct: '#f59e0b', transplanted: '#10b981' };

  if (groups.length === 0) return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted, #5e7265)' }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>🌱</div>
      <div style={{ fontWeight: 700 }}>Nothing ready yet</div>
      <div style={{ fontSize: 13, marginTop: 6 }}>Trays in progress will appear here as they mature.</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {groups.map(([cropName, trays], gi) => {
        const isRepeat   = trays[0].harvestType === 'repeat';
        const isOpen     = !!openGroups[cropName];
        const maxAge     = Math.max(...trays.map(r => r.ageDays));
        const anyOverdue = !isRepeat && trays.some(r => r.ageDays > r.targetDays);
        const accentColor = isRepeat ? '#10b981' : anyOverdue ? '#88b82e' : '#10b981';

        return (
          <div key={cropName} style={{
            border: '1px solid var(--border, #d4e0d4)',
            borderLeft: `4px solid ${accentColor}`,
            borderRadius: 'var(--r-lg)', overflow: 'hidden',
            background: 'var(--panel, #fff)',
            boxShadow: 'var(--shadow-card)',
            ...stagger(gi, 'td-fade-up', 0, 40, 300),
          }}>
            {/* Group header - click to expand */}
            <div
              role="button" tabIndex={0}
              onClick={() => toggleGroup(cropName)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleGroup(cropName); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '13px 16px', cursor: 'pointer', userSelect: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{cropName}</div>

                {/* Tray count badge */}
                <span style={{
                  fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '2px 9px',
                  background: `${accentColor}22`, color: accentColor, border: `1px solid ${accentColor}55`,
                }}>
                  {trays.length} tray{trays.length !== 1 ? 's' : ''}
                </span>

                {/* Status badge */}
                {isRepeat ? (
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: '#10b981',
                    background: '#10b98115', border: '1px solid #10b98155',
                    borderRadius: 999, padding: '2px 8px',
                  }}>✂ Repeat harvest</span>
                ) : anyOverdue ? (
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: '#ef4444',
                    background: '#ef444415', border: '1px solid #ef444455',
                    borderRadius: 999, padding: '2px 8px',
                  }}>⚠ Ready</span>
                ) : (
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: '#10b981',
                    background: '#10b98115', border: '1px solid #10b98155',
                    borderRadius: 999, padding: '2px 8px',
                  }}>✓ Ready</span>
                )}

                {!isRepeat && (
                  <span style={{ fontSize: 11, color: 'var(--muted, #5e7265)' }}>
                    oldest: {maxAge}d
                  </span>
                )}
              </div>

              <div style={{
                fontSize: 18, color: 'var(--muted, #5e7265)',
                transform: isOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform .2s',
              }}>⌄</div>
            </div>

            {/* Expanded: individual trays */}
            {isOpen && (
              <div style={{ borderTop: '1px solid var(--border, #d4e0d4)' }}>
                {trays.map((row, ti) => {
                  const { benchName, layerIndex, trayIndex, half, tray, ageDays, stage } = row;
                  const sc    = stageColors[stage] || '#999';
                  const notes = (tray.notes || '').trim();
                  return (
                    <div
                      key={`${benchName}-${layerIndex}-${trayIndex}-${half ?? 'f'}`}
                      role="button" tabIndex={0}
                      onClick={() => onTrayClick(benchName, layerIndex, trayIndex, half)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onTrayClick(benchName, layerIndex, trayIndex, half); }}
                      className="tray-summary-ready-item"
                      style={{
                        padding: '10px 16px 10px 20px',
                        borderBottom: ti < trays.length - 1 ? '1px solid var(--border, #d4e0d4)' : 'none',
                        cursor: 'pointer',
                        background: 'var(--panel-2, #eef2ee)',
                        transition: 'background .12s',
                        ...stagger(ti, 'td-fade-up', 0, 30, 250),
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--panel, #fff)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--panel-2, #eef2ee)'}
                    >
                      {/* Location + seeded date */}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {benchName} · L{layerIndex + 1} T{trayIndex + 1}{half != null ? `·${halfLabel(half)} (½)` : ''}
                        </div>
                        {tray.seededAt && (
                          <div style={{ fontSize: 11, color: 'var(--muted, #5e7265)', marginTop: 1 }}>
                            Seeded {tray.seededAt}
                          </div>
                        )}
                      </div>

                      {/* Stage */}
                      <StagePill stage={stage} />

                      {/* Age */}
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: sc, marginBottom: 3 }}>{ageDays}d old</div>
                        <div style={{ height: 4, borderRadius: 999, background: 'var(--border, #d4e0d4)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 999, background: sc, width: `${Math.min(100, (ageDays / 60) * 100)}%` }} />
                        </div>
                      </div>

                      {/* Notes */}
                      <div style={{
                        fontSize: 12, color: 'var(--muted)', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontStyle: notes ? 'normal' : 'italic', opacity: notes ? 1 : .45,
                      }}>
                        {notes || 'No notes'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BoxReadinessView({ allTrays, seedInventory }) {
  const [boxes, setBoxes] = useState([]);
  const [loadingBoxes, setLoadingBoxes] = useState(false);
  const [openBoxes, setOpenBoxes] = useState({});
  const [windowDays, setWindowDays] = useState(7);
 
  useEffect(() => {
    setLoadingBoxes(true);
    axiosClient.get('/boxes?all=1')
      .then((r) => setBoxes(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error('BoxReadinessView load error', e))
      .finally(() => setLoadingBoxes(false));
  }, []);
 
  const enrichedBoxes = useMemo(() => {
    return boxes.map((box) => ({
      ...box,
      readiness: calcBoxReadiness(box, allTrays, seedInventory),
    }));
  }, [boxes, allTrays, seedInventory]);
 
  // Sort: ready first, then by overallDays asc, missing last
  const sorted = useMemo(() => [...enrichedBoxes].sort((a, b) => {
    const { overallDays: ad, anyMissing: am } = a.readiness;
    const { overallDays: bd, anyMissing: bm } = b.readiness;
    if (am && !bm) return 1;
    if (!am && bm) return -1;
    if (ad == null) return 1;
    if (bd == null) return -1;
    return ad - bd;
  }), [enrichedBoxes]);
 
  // Summary strip
  const readyNow   = sorted.filter((b) => b.readiness.allReady).length;
  const withinWindow = sorted.filter((b) => !b.readiness.anyMissing && b.readiness.overallDays != null && b.readiness.overallDays <= windowDays && b.readiness.overallDays > 0).length;
  const missing    = sorted.filter((b) => b.readiness.anyMissing).length;
 
  const statusColor = (status) =>
    status === 'ready'   ? '#10b981'
    : status === 'soon'  ? '#f59e0b'
    : status === 'growing' ? '#3b82f6'
    : '#ef4444'; // missing
 
  const overallBadge = (r) => {
    if (r.anyMissing)          return { label: '⚠ Missing crops', color: '#ef4444', bg: '#fef2f2' };
    if (r.allReady)            return { label: '✓ Ready now',      color: '#10b981', bg: '#f0fdf4' };
    if (r.overallDays <= windowDays) return { label: `📅 Ready in ${r.overallDays}d`, color: '#f59e0b', bg: '#fffbeb' };
    return { label: `${r.overallDays}d away`, color: '#3b82f6', bg: '#eff6ff' };
  };
 
  if (loadingBoxes) return (
    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>Loading boxes…</div>
  );
 
  if (!boxes.length) return (
    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
      <div style={{ fontWeight: 700 }}>No boxes found</div>
      <div style={{ fontSize: 13, marginTop: 4 }}>Create boxes in the Products page first.</div>
    </div>
  );
 
  return (
    <div>
      {/* Controls + stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flex: 1 }}>
          {[
            { label: 'Ready now',           value: readyNow,       color: '#10b981' },
            { label: `Within ${windowDays}d`, value: withinWindow, color: '#f59e0b' },
            { label: 'Missing crops',        value: missing,       color: '#ef4444' },
          ].map((s, i) => (
            <div key={i} style={{
              background: 'var(--panel, #fff)', border: '1px solid var(--border, #d4e0d4)',
              borderTop: `3px solid ${s.color}`,
              borderRadius: 'var(--r-lg)', padding: '10px 16px', minWidth: 110,
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
 
        {/* Window slider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)' }}>
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Window:</span>
          <input
            type="range" min={1} max={30} value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            style={{ width: 100 }}
          />
          <span style={{ fontWeight: 700, minWidth: 28 }}>{windowDays}d</span>
        </div>
      </div>
 
      {/* Box cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sorted.map((box, bi) => {
          const { readiness } = box;
          const badge = overallBadge(readiness);
          const isOpen = !!openBoxes[box.id];
 
          return (
            <div key={box.id} style={{
              border: '1px solid var(--border, #d4e0d4)',
              borderLeft: `4px solid ${badge.color}`,
              borderRadius: 'var(--r-lg)', overflow: 'hidden',
              background: 'var(--panel, #fff)',
              boxShadow: 'var(--shadow-card)',
              ...stagger(bi, 'td-fade-up', 0, 40, 300),
            }}>
              {/* Box header */}
              <div
                role="button" tabIndex={0}
                onClick={() => setOpenBoxes((p) => ({ ...p, [box.id]: !p[box.id] }))}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpenBoxes((p) => ({ ...p, [box.id]: !p[box.id] })); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '13px 16px', cursor: 'pointer', userSelect: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{box.name}</div>
                  {box.grams && (
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{box.grams}</span>
                  )}
                  {!box.enabled && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: 'var(--muted)',
                      background: 'var(--panel-2)', border: '1px solid var(--border)',
                      borderRadius: 999, padding: '2px 7px',
                    }}>Hidden</span>
                  )}
 
                  {/* Overall readiness badge */}
                  <span style={{
                    fontSize: 12, fontWeight: 700, color: badge.color,
                    background: badge.bg, border: `1px solid ${badge.color}33`,
                    borderRadius: 999, padding: '3px 10px',
                  }}>{badge.label}</span>
 
                  {/* Per-item quick pills */}
                  {readiness.items.map((item) => (
                    <span key={item.cropName} style={{
                      fontSize: 11, borderRadius: 999, padding: '2px 8px',
                      background: `${statusColor(item.status)}18`,
                      border: `1px solid ${statusColor(item.status)}44`,
                      color: statusColor(item.status),
                      fontWeight: 600,
                    }}>
                      {item.cropName}
                      {item.status === 'ready'   && ' ✓'}
                      {item.status === 'soon'    && ` ~${item.daysUntilReady}d`}
                      {item.status === 'growing' && ` ${item.daysUntilReady}d`}
                      {item.status === 'missing' && ' ✕'}
                    </span>
                  ))}
                </div>
 
                <div style={{
                  fontSize: 18, color: 'var(--muted)',
                  transform: isOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform .2s',
                }}>⌄</div>
              </div>
 
              {/* Expanded: per-crop detail */}
              {isOpen && (
                <div style={{ borderTop: '1px solid var(--border, #d4e0d4)' }}>
                  {readiness.items.map((item, ii) => {
                    const sc = statusColor(item.status);
                    const pct = item.bestTray && item.targetDays
                      ? Math.min(100, Math.round((item.bestTray.ageDays / item.targetDays) * 100))
                      : item.status === 'ready' ? 100 : 0;
 
                    return (
                      <div
                        key={item.cropName}
                        className="tray-box-item"
                        style={{
                          padding: '10px 16px 10px 20px',
                          borderBottom: ii < readiness.items.length - 1
                            ? '1px solid var(--border, #d4e0d4)' : 'none',
                          background: 'var(--panel-2, #eef2ee)',
                          ...stagger(ii, 'td-fade-up', 0, 30, 250),
                        }}
                      >
                        {/* Crop name + status */}
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{item.cropName}</div>
                          <div style={{ fontSize: 11, color: sc, fontWeight: 600, marginTop: 2 }}>
                            {item.status === 'ready'   && '✓ Ready'}
                            {item.status === 'soon'    && `📅 Ready in ${item.daysUntilReady} day${item.daysUntilReady !== 1 ? 's' : ''}`}
                            {item.status === 'growing' && `⏳ ${item.daysUntilReady} days left`}
                            {item.status === 'missing' && '⚠ No trays seeded'}
                          </div>
                        </div>
 
                        {/* Tray count */}
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {item.trays.length > 0
                            ? `${item.trays.length} tray${item.trays.length !== 1 ? 's' : ''}`
                            : '-'
                          }
                        </div>
 
                        {/* Progress bar */}
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 11, color: sc, fontWeight: 700 }}>{pct}%</span>
                            {item.bestTray && (
                              <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                                {item.bestTray.ageDays}d grown
                              </span>
                            )}
                          </div>
                          <div style={{ height: 5, borderRadius: 999, background: 'var(--border, #d4e0d4)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: 999, background: sc, width: `${pct}%`, transition: 'width .5s ease' }} />
                          </div>
                        </div>
 
                        {/* Best tray location */}
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {item.bestTray
                            ? `${item.bestTray.benchName} · L${item.bestTray.layerIndex + 1} T${item.bestTray.trayIndex + 1}${item.bestTray.half != null ? `·${halfLabel(item.bestTray.half)}` : ''}`
                            : '-'
                          }
                          {item.trays.length > 1 && (
                            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--muted)', opacity: .7 }}>
                              (+{item.trays.length - 1} more)
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main SummaryView ─────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// Farm map
//
// Draws the benches as they physically are so the advice can be
// read at a glance rather than followed line by line: flip between
// "Now" and "After" to see what changes, with colour showing how
// close each tray is to harvest and shading showing how hard a slot
// is to reach. Geometry comes from the server (which layers exist,
// which end you reach from, where the micro zone is) so the rules
// live in one place.
// ─────────────────────────────────────────────────────────────

const URGENCY_STYLES = [
  { max: 0,        bg: '#dcfce7', border: '#86efac', text: '#166534' }, // ready
  { max: 7,        bg: '#fef3c7', border: '#fcd34d', text: '#92400e' }, // within a week
  { max: 21,       bg: '#e0f2fe', border: '#bae6fd', text: '#075985' }, // coming up
  { max: Infinity, bg: '#f3f4f6', border: '#e5e7eb', text: '#4b5563' }, // a way off
];

function urgencyStyle(daysToGo) {
  if (daysToGo == null) return URGENCY_STYLES[3];
  return URGENCY_STYLES.find((s) => daysToGo <= s.max);
}

// Cells are narrow, so drop the redundant "Salad - " prefix and let
// the rest wrap over two lines rather than truncating to initials.
function shortCrop(name) {
  return String(name || '')
    .replace(/^Salad\s*-\s*/i, '')
    .replace(/\bMicro\s+/i, '');
}

// Walkthrough markers: which slot to lift a tray from, and where it goes.
const MARK_STYLES = {
  pick: { colour: '#ea580c', label: '1',   text: 'PICK UP' },
  put:  { colour: '#2563eb', label: '2',   text: 'PUT HERE' },
  swap: { colour: '#2563eb', label: '2 ⇄', text: 'SWAP' },
};

function MarkBadge({ mark }) {
  const m = MARK_STYLES[mark];
  if (!m) return null;
  return (
    <span style={{
      position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, padding: '0 4px',
      borderRadius: 999, background: m.colour, color: '#fff', fontSize: 10, fontWeight: 800,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
    }}>
      {m.label}
    </span>
  );
}

function MapTray({ entry, view, selected, onSelect, half, mark }) {
  const markStyle = MARK_STYLES[mark];
  const minHeight = half ? 25 : 52;

  if (!entry) {
    if (markStyle) {
      return (
        <div data-mark={mark} style={{
          position: 'relative', flex: 1, minHeight, borderRadius: 'var(--r-sm, 4px)',
          border: `2px dashed ${markStyle.colour}`, background: '#eff6ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: half ? 8 : 10, fontWeight: 800, color: markStyle.colour,
        }}>
          {markStyle.text}
          <MarkBadge mark={mark} />
        </div>
      );
    }
    return (
      <div style={{
        flex: 1, minHeight, borderRadius: 'var(--r-sm, 4px)',
        border: '1px dashed var(--line, #d1d5db)', background: 'transparent',
      }} />
    );
  }

  const style = urgencyStyle(entry.daysToGo);
  const isSelected = selected === entry.trayKey;
  const stepView = view === 'step';
  // In "After" a tray that is not staying put has just arrived here;
  // in "Now" the same tray is the one about to leave.
  const moving = !stepView && !entry.inPlace;
  // During a walkthrough step everything not involved fades back, so the
  // eye goes straight to the two marked slots.
  const opacity = stepView ? (mark ? 1 : 0.4) : (moving && view === 'now' ? 0.55 : 1);

  let border = `1px solid ${style.border}`;
  if (markStyle) border = `3px solid ${markStyle.colour}`;
  else if (isSelected) border = '2px solid #2563eb';

  const basePadding = half ? '2px 5px' : '4px 6px';

  return (
    <button
      type="button"
      data-mark={mark || undefined}
      onClick={onSelect ? () => onSelect(isSelected ? null : entry.trayKey) : undefined}
      title={`${entry.cropType} · ${entry.seededAt || 'no seed date'} · ${
        entry.daysToGo <= 0 ? 'ready' : `${entry.daysToGo}d to go`
      }`}
      style={{
        position: 'relative',
        flex: 1, minWidth: 0, minHeight, textAlign: 'left',
        padding: basePadding, paddingRight: markStyle ? (mark === 'swap' ? 30 : 22) : undefined,
        cursor: onSelect ? 'pointer' : 'default',
        borderRadius: 'var(--r-sm, 4px)',
        background: style.bg,
        color: style.text,
        border,
        outline: moving && view === 'after' ? '2px dashed #2563eb' : 'none',
        outlineOffset: -3,
        opacity,
        overflow: 'hidden',
      }}
    >
      <div style={{
        fontSize: half ? 8.5 : 10.5, fontWeight: 700, lineHeight: 1.1,
        display: '-webkit-box', WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical', overflow: 'hidden',
        overflowWrap: 'break-word', hyphens: 'none',
      }}>
        {entry.inChamber && view === 'after' && '✳ '}
        {shortCrop(entry.cropType)}
      </div>
      {!half && (
        <div style={{ fontSize: 10, opacity: 0.85, fontWeight: 600 }}>
          {entry.daysToGo <= 0 ? 'ready' : `${entry.daysToGo}d`}
          {entry.maturityEstimated && '?'}
        </div>
      )}
      <MarkBadge mark={mark} />
    </button>
  );
}

function MapCell({ base, cell, entryByKey, view, selected, onSelect, cost, access, marks }) {
  const fullMark = marks?.[base];
  const halfMarks = [marks?.[`${base}/h0`], marks?.[`${base}/h1`]];
  // A half tray arriving in an empty cell still needs to show which half.
  const split = !!(cell?.halves || halfMarks[0] || halfMarks[1]);
  const resolve = (key) => (key ? entryByKey[key] || null : null);
  // Reach cost shades the cell from behind: the deeper into a
  // front-access bench, the darker the backing.
  const shade = access === 'front' && cost > 0 ? 0.04 * cost : 0;

  return (
    <div style={{
      flex: '1 0 84px', minWidth: 84, display: 'flex', flexDirection: 'column', gap: 2,
      padding: 2, borderRadius: 'var(--r-sm, 4px)',
      background: shade ? `rgba(15, 23, 42, ${shade})` : 'transparent',
    }}>
      {split ? (
        <>
          <MapTray entry={resolve(cell?.halves?.[0])} view={view} selected={selected} onSelect={onSelect} half mark={halfMarks[0]} />
          <MapTray entry={resolve(cell?.halves?.[1])} view={view} selected={selected} onSelect={onSelect} half mark={halfMarks[1]} />
        </>
      ) : (
        <MapTray entry={resolve(cell?.full)} view={view} selected={selected} onSelect={onSelect} mark={fullMark} />
      )}
    </div>
  );
}

// On a phone a bench is wider than the screen, so the slot a walkthrough
// step is pointing at can sit hidden off the side. This scrolls each bench
// sideways to centre its marked slots (or show the first, if pick-up and
// put-down are too far apart to fit together).
function BenchScroller({ marks, children }) {
  const ref = useRef(null);
  const markKey = marks ? Object.entries(marks).map((e) => e.join('=')).sort().join('|') : '';

  useEffect(() => {
    const scroller = ref.current;
    if (!scroller || !markKey) return undefined;

    const adjust = () => {
      const marked = [...scroller.querySelectorAll('[data-mark]')];
      if (!marked.length) return;

      const box = scroller.getBoundingClientRect();
      const rects = marked.map((el) => el.getBoundingClientRect());
      const left = Math.min(...rects.map((r) => r.left));
      const right = Math.max(...rects.map((r) => r.right));
      // Leave room for the pinned layer label on the left edge.
      const labelWidth = 36;

      scroller.scrollLeft += right - left <= box.width - labelWidth
        ? (left + right) / 2 - (box.left + labelWidth + (box.width - labelWidth) / 2)
        : left - box.left - labelWidth;
    };

    adjust();
    // Measure again once the step's layout has settled: a change of the
    // same size in the same place (e.g. the two halves of one cell on
    // consecutive steps) can otherwise leave the scroll where it was.
    const frame = requestAnimationFrame(adjust);
    return () => cancelAnimationFrame(frame);
  }, [markKey]);

  // overflowAnchor off: the browser's scroll anchoring would otherwise
  // shift the bench back after we have scrolled it.
  return <div ref={ref} style={{ overflowX: 'auto', overflowAnchor: 'none' }}>{children}</div>;
}

// state comes from buildStepStates: the farm now, after all moves, or at
// a particular walkthrough step. onlyBenches narrows a step to the benches
// it touches, so a phone is not scrolling past five irrelevant ones.
function FarmMap({ benches, state, entryByKey, view, selected, onSelect, marks, onlyBenches }) {
  const shown = onlyBenches ? benches.filter((b) => onlyBenches.includes(b.name)) : benches;

  return (
    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))' }}>
      {shown.map((bench) => {
        const involved = !!marks && Object.keys(marks).some((id) => id.startsWith(`${bench.name}/`));
        return (
          <div key={bench.name} style={{
            border: involved ? '2px solid #93c5fd' : '1px solid var(--line, #e5e7eb)',
            borderRadius: 'var(--r-md)', padding: '8px 10px 10px', background: 'var(--card, #fff)',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
              <strong style={{ fontSize: 13 }}>{bench.label}</strong>
              <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>
                {bench.access === 'full' ? 'walk-around' : 'reach from slot 1'}
              </span>
            </div>

            {/* One scroller per bench so a phone swipes the whole bench at
                once; the layer label stays pinned to the left edge. */}
            <BenchScroller marks={marks}>
              <div>
                {/* Layer 1 is the bottom shelf, so draw from the top down -
                    the same way round as the Layout tab. */}
                {[...bench.layers].reverse().map((layer) => (
                  <div key={layer.index} style={{ display: 'flex', alignItems: 'stretch', gap: 6, marginBottom: 4 }}>
                    <div style={{
                      position: 'sticky', left: 0, zIndex: 1,
                      background: 'var(--card, #fff)', paddingRight: 2,
                      flex: '0 0 30px', fontSize: 10, color: 'var(--muted)', fontWeight: 700,
                      display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    }}>
                      <span>L{layer.index + 1}</span>
                      {layer.micro && <span style={{ fontSize: 8, opacity: 0.8 }}>micro</span>}
                    </div>

                    {layer.installed ? (
                      <div style={{ display: 'flex', gap: 2, flex: 1 }}>
                        {Array.from({ length: layer.slots }, (_, t) => {
                          const base = `${bench.name}/${layer.index}/${t}`;
                          return (
                            <MapCell
                              key={t}
                              base={base}
                              cell={state?.cells?.[base]}
                              entryByKey={entryByKey}
                              view={view}
                              selected={selected}
                              onSelect={onSelect}
                              cost={layer.costs[t]}
                              access={bench.access}
                              marks={marks}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{
                        flex: 1, minWidth: 200, minHeight: 26, borderRadius: 'var(--r-sm, 4px)',
                        border: '1px dashed var(--line, #d1d5db)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, color: 'var(--muted)', fontStyle: 'italic',
                      }}>
                        not installed yet
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </BenchScroller>

            {bench.access === 'front' && (
              <div style={{ fontSize: 9, color: 'var(--muted)', marginLeft: 36, marginTop: 2 }}>
                ← easy · deep →
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MapLegend({ walkthrough }) {
  const items = [
    { label: 'ready', ...URGENCY_STYLES[0] },
    { label: '≤ 7d',  ...URGENCY_STYLES[1] },
    { label: '≤ 21d', ...URGENCY_STYLES[2] },
    { label: 'later', ...URGENCY_STYLES[3] },
  ];
  const chip = { display: 'inline-flex', alignItems: 'center', gap: 4 };
  const badge = (colour, label) => (
    <span style={{
      minWidth: 16, height: 16, padding: '0 4px', borderRadius: 999, background: colour,
      color: '#fff', fontSize: 10, fontWeight: 800, display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center',
    }}>{label}</span>
  );

  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 11, color: 'var(--muted)' }}>
      {walkthrough && (
        <>
          <span style={chip}>{badge(MARK_STYLES.pick.colour, '1')} pick up</span>
          <span style={chip}>{badge(MARK_STYLES.put.colour, '2')} put down</span>
          <span style={chip}>{badge(MARK_STYLES.swap.colour, '2 ⇄')} swap</span>
        </>
      )}
      {items.map((i) => (
        <span key={i.label} style={chip}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: i.bg, border: `1px solid ${i.border}` }} />
          {i.label}
        </span>
      ))}
      {!walkthrough && (
        <>
          <span style={chip}>
            <span style={{ width: 11, height: 11, borderRadius: 3, border: '2px dashed #2563eb' }} />
            moving
          </span>
          <span>✳ from chamber</span>
        </>
      )}
      <span>? estimated timing</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Arrange tab
//
// Shows the advice from GET /bench-layout/arrangement: which trays
// want moving so that a crop stays in one block and whatever is
// closest to harvest sits in the easiest-to-reach slots, plus where
// each germination-chamber tray should go when it comes out.
//
// The move list is an ordered sequence - a later step can depend on
// an earlier one having freed a slot - so applying one step refetches
// the advice rather than carrying on with a stale list.
// ─────────────────────────────────────────────────────────────

function daysLabel(d) {
  if (d == null) return '—';
  if (d <= 0) return `ready${d < 0 ? ` · ${Math.abs(d)}d over` : ''}`;
  return `${d}d away`;
}

function daysColour(d) {
  if (d == null) return 'var(--muted)';
  if (d <= 0) return '#10b981';
  if (d <= 7) return '#f59e0b';
  return 'var(--muted)';
}

function ArrangeStat({ label, value, colour }) {
  return (
    <div style={{
      flex: '1 1 110px', minWidth: 110, background: 'var(--card, #fff)',
      border: '1px solid var(--line, #e5e7eb)', borderRadius: 'var(--r-md)',
      padding: '10px 12px',
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: colour || 'var(--ink, #111)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function MoveRow({ move, onApply, busy, selected, onSelect }) {
  return (
    <li
      onClick={onSelect}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 8px',
        borderTop: '1px solid var(--line, #e5e7eb)', cursor: 'pointer',
        background: selected ? '#eff6ff' : 'transparent',
        borderRadius: selected ? 'var(--r-sm, 4px)' : 0,
      }}
    >
      <span style={{
        flex: '0 0 26px', height: 26, borderRadius: 999, background: 'var(--brand-2, #2e7d32)',
        color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>{move.step}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span>{move.cropType}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: daysColour(move.daysToGo) }}>
            {daysLabel(move.daysToGo)}
          </span>
          {move.size === 'half' && (
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', border: '1px solid var(--line,#e5e7eb)', borderRadius: 'var(--r-sm, 4px)', padding: '1px 5px' }}>
              HALF
            </span>
          )}
          {move.fromChamber && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#ede9fe', borderRadius: 'var(--r-sm, 4px)', padding: '1px 5px' }}>
              FROM CHAMBER
            </span>
          )}
        </div>

        <div style={{ fontSize: 13, marginTop: 2 }}>
          <span style={{ color: 'var(--muted)' }}>{move.fromLabel}</span>
          <span style={{ margin: '0 6px' }}>→</span>
          <strong>{move.toLabel}</strong>
          {move.swap && (
            <span style={{ color: 'var(--muted)' }}> (swap with {move.swapWith})</span>
          )}
        </div>

        {move.reason && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{move.reason}</div>
        )}
      </div>

      <button
        className="btn-tab"
        disabled={busy}
        onClick={(e) => { e.stopPropagation(); onApply(move); }}
        style={{ flex: '0 0 auto', fontSize: 12, padding: '5px 10px' }}
      >
        {busy ? '…' : 'Do it'}
      </button>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────
// Move walkthrough
//
// One move at a time, for doing the job in the farm. Each step shows the
// farm as it will be at that point (after the earlier steps), marks the
// slot to pick up from and the slot to put down in, and says it in words.
//
// "Done" saves that move straight away so the layout stays in step with
// what has physically happened. Only the next undone step can be marked
// done: later steps rely on earlier ones having freed or swapped a slot,
// so looking ahead is allowed but doing them out of order is not.
// ─────────────────────────────────────────────────────────────

const STEP_CARD = {
  background: 'var(--card, #fff)', border: '1px solid var(--line, #e5e7eb)',
  borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 12,
};

function StepBadge({ colour, children }) {
  return (
    <span style={{
      flex: '0 0 auto', minWidth: 22, height: 22, padding: '0 6px', borderRadius: 999,
      background: colour, color: '#fff', fontSize: 12, fontWeight: 800,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>{children}</span>
  );
}

function StepLine({ badge, colour, label, where }) {
  return (
    <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0' }}>
      <StepBadge colour={colour}>{badge}</StepBadge>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{label}</div>
        {where && <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{where}</div>}
      </div>
    </li>
  );
}

function MoveWalkthrough({ data, steps, entryByKey, onClose, onApplied }) {
  const total = data.moves.length;
  const [step, setStep]           = useState(0);
  const [done, setDone]           = useState(0);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState(null);
  const [wholeFarm, setWholeFarm] = useState(false);
  const topRef = useRef(null);

  const chamberLocationByTrayId = useMemo(
    () => Object.fromEntries(data.chamber.map((c) => [c.trayId, c.chamberLocation])),
    [data],
  );

  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [step]);

  const move = data.moves[step];

  const markDone = useCallback(async () => {
    if (!move || step !== done || busy) return;
    setBusy(true);
    setError(null);
    try {
      await axiosClient.post('/bench-layout/move', { from: move.from, to: move.to });
      setDone((d) => d + 1);
      setStep((s) => s + 1);
      if (onApplied) onApplied();
    } catch (err) {
      setError(err?.response?.data?.error || 'That move could not be saved.');
    } finally {
      setBusy(false);
    }
  }, [move, step, done, busy, onApplied]);

  if (done >= total) {
    return (
      <div ref={topRef} style={{ ...STEP_CARD, textAlign: 'center', padding: '28px 16px' }}>
        <div style={{ fontSize: 34, color: '#10b981' }}>✓</div>
        <h3 style={{ margin: '6px 0', fontSize: 18 }}>All {total} moves done</h3>
        <p style={{ color: 'var(--muted)', fontSize: 13, margin: '0 0 14px' }}>
          Each move was saved as you went. Recheck to see if anything else is worth moving.
        </p>
        <button className="btn-tab active" onClick={() => onClose(true)}>Recheck the farm</button>
      </div>
    );
  }

  const before = steps.states[step];
  const conflict = steps.conflicts[step];
  const fromChamber = move.fromChamber || move.from?.bench === 'germinationChamber';
  const text = describeStep(move, { benches: data.benches, chamberLocationByTrayId });

  const marks = {};
  if (!fromChamber) marks[positionId(move.from)] = 'pick';
  marks[positionId(move.to)] = move.swap ? 'swap' : 'put';

  const isDone = step < done;
  const isAhead = step > done;

  return (
    <div ref={topRef} style={{ scrollMarginTop: 8 }}>
      {/* ── The instruction ── */}
      <div style={STEP_CARD}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <strong style={{ fontSize: 15 }}>Step {step + 1} of {total}</strong>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{done} done</span>
          <button
            className="btn-tab"
            style={{ marginLeft: 'auto', fontSize: 12, padding: '5px 10px' }}
            onClick={() => onClose(done > 0)}
            disabled={busy}
          >
            Exit
          </button>
        </div>

        <div style={{ height: 6, borderRadius: 999, background: 'var(--line, #e5e7eb)', margin: '8px 0 12px', overflow: 'hidden' }}>
          <div style={{ width: `${(done / total) * 100}%`, height: '100%', background: '#10b981', transition: 'width .25s' }} />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
          <span style={{ fontSize: 20, fontWeight: 800 }}>{text.cropType}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: daysColour(move.daysToGo) }}>{daysLabel(move.daysToGo)}</span>
          {text.half && (
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', border: '1px solid var(--line,#e5e7eb)', borderRadius: 'var(--r-sm, 4px)', padding: '1px 5px' }}>
              HALF TRAY
            </span>
          )}
          {isDone && <span style={{ fontSize: 12, fontWeight: 700, color: '#10b981' }}>✓ done</span>}
        </div>

        <ol style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
          <StepLine badge="1" colour={MARK_STYLES.pick.colour} label="Pick it up from" where={text.pickUp} />
          {text.swap && (
            <StepLine
              badge="⇄"
              colour="#7c3aed"
              label={`${text.swap.cropType} is sitting there — lift it out first`}
            />
          )}
          <StepLine badge="2" colour={MARK_STYLES.put.colour} label="Put it in" where={text.putDown} />
          {text.swap && (
            <StepLine
              badge="3"
              colour="#7c3aed"
              label={`Put the ${text.swap.cropType} where the ${text.cropType} came from`}
              where={text.swap.goesTo}
            />
          )}
        </ol>

        {move.reason && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>Why: {move.reason}</div>
        )}

        {conflict && (
          <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 'var(--r-md)', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 13 }}>
            This step doesn&apos;t match the farm the way the list expects, so the list may be out of date.{' '}
            <button className="btn-tab" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => onClose(true)}>Recheck</button>
          </div>
        )}

        {error && (
          <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 'var(--r-md)', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>
            Couldn&apos;t save this move: {error}. If the farm has changed, recheck for a fresh list.{' '}
            <button className="btn-tab" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => onClose(true)}>Recheck</button>
          </div>
        )}
      </div>

      {/* ── The chamber, when that is where the tray comes from ── */}
      {fromChamber && (
        <div style={STEP_CARD}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Germination chamber</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {before.chamber.map((key) => {
              const e = entryByKey[key];
              const picked = key === move.trayKey;
              const loc = e ? chamberLocationByTrayId[e.trayId] : null;
              return (
                <span key={key} style={{
                  position: 'relative', fontSize: 12, borderRadius: 'var(--r-sm, 4px)',
                  padding: picked ? '6px 26px 6px 8px' : '6px 8px',
                  border: picked ? `3px solid ${MARK_STYLES.pick.colour}` : '1px solid var(--line, #e5e7eb)',
                  opacity: picked ? 1 : 0.45,
                }}>
                  <strong>{loc || '—'}</strong> {e ? shortCrop(e.cropType) : ''}
                  {picked && <MarkBadge mark="pick" />}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Where to look ── */}
      <div style={STEP_CARD}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>
            {wholeFarm ? 'Whole farm' : 'Where to look'}{' '}
            <span style={{ color: 'var(--muted)', fontWeight: 500 }}>· before this move</span>
          </h3>
          <button className="btn-tab" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setWholeFarm((v) => !v)}>
            {wholeFarm ? 'Just these benches' : 'Show whole farm'}
          </button>
        </div>

        <FarmMap
          benches={data.benches}
          state={before}
          entryByKey={entryByKey}
          view="step"
          marks={marks}
          onlyBenches={wholeFarm ? null : benchesForMove(move)}
        />
        <div style={{ marginTop: 10 }}><MapLegend walkthrough /></div>
      </div>

      {isAhead && (
        <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', marginBottom: 8 }}>
          You&apos;re looking ahead. Do step {done + 1} first, because later steps depend on the earlier ones.
        </div>
      )}

      {/* ── Actions, kept in reach at the bottom of the screen ── */}
      <div style={{
        position: 'sticky', bottom: 0, zIndex: 5, display: 'flex', gap: 8,
        padding: '10px 0', background: 'rgba(255,255,255,0.95)',
        borderTop: '1px solid var(--line, #e5e7eb)',
      }}>
        <button className="btn-tab" disabled={step === 0 || busy} onClick={() => setStep((s) => s - 1)}>
          ‹ Back
        </button>

        {isDone && (
          <button className="btn-tab" disabled style={{ flex: 1 }}>✓ Already done</button>
        )}
        {isAhead && (
          <button className="btn-tab active" style={{ flex: 1 }} onClick={() => setStep(done)}>
            Go to step {done + 1}
          </button>
        )}
        {!isDone && !isAhead && (
          <button
            className="btn-tab active"
            style={{ flex: 1, fontWeight: 800, fontSize: 14 }}
            disabled={busy || conflict}
            onClick={markDone}
          >
            {busy ? 'Saving…' : '✓ Done — next'}
          </button>
        )}

        <button className="btn-tab" disabled={step >= total - 1 || busy} onClick={() => setStep((s) => s + 1)}>
          Next ›
        </button>
      </div>
    </div>
  );
}

function ArrangeView({ onApplied }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(null);   // step number, or 'all'
  const [showAll, setShowAll] = useState(false);
  const [view, setView]       = useState('after'); // 'now' | 'after'
  const [selected, setSelected] = useState(null);  // trayKey
  const [walking, setWalking]   = useState(false);
  // Remembered per device: some days the chamber trays aren't ready even
  // though they're due out by date.
  const [includeChamber, setIncludeChamber] = useState(() => {
    try {
      return localStorage.getItem('arrange.includeChamber') !== 'false';
    } catch {
      return true;
    }
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosClient.get('/bench-layout/arrangement', {
        params: { includeChamber },
      });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not work out an arrangement');
    } finally {
      setLoading(false);
    }
  }, [includeChamber]);

  const toggleChamber = useCallback((on) => {
    setIncludeChamber(on);
    try {
      localStorage.setItem('arrange.includeChamber', on ? 'true' : 'false');
    } catch {
      // Storage unavailable: the switch still works for this visit.
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyMove = useCallback(async (move) => {
    setBusy(move.step);
    try {
      await axiosClient.post('/bench-layout/move', { from: move.from, to: move.to });
      await load();
      if (onApplied) await onApplied();
    } catch (err) {
      alert(err?.response?.data?.error || 'Move failed');
    } finally {
      setBusy(null);
    }
  }, [load, onApplied]);

  const applyAll = useCallback(async () => {
    if (!data?.moves?.length) return;
    if (!window.confirm(`Apply all ${data.moves.length} moves? This rearranges the farm in one go.`)) return;

    setBusy('all');
    try {
      for (const move of data.moves) {
        // Stop at the first failure: later steps assume the earlier
        // ones happened, so carrying on would put trays in the wrong place.
        await axiosClient.post('/bench-layout/move', { from: move.from, to: move.to });
      }
    } catch (err) {
      alert(`Stopped partway: ${err?.response?.data?.error || 'move failed'}. The remaining steps have been recalculated.`);
    } finally {
      setBusy(null);
      await load();
      if (onApplied) await onApplied();
    }
  }, [data, load, onApplied]);

  const entryByKey = useMemo(
    () => Object.fromEntries((data?.plan || []).map((p) => [p.trayKey, p])),
    [data],
  );
  // states[0] is the farm now; the last entry is the farm once every move is done.
  const steps = useMemo(
    () => buildStepStates(data?.plan || [], data?.moves || []),
    [data],
  );

  if (loading && !data) {
    return <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>Working out the best arrangement…</div>;
  }
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: '#b91c1c' }}>
        {error}
        <div><button className="btn-tab" style={{ marginTop: 12 }} onClick={load}>Try again</button></div>
      </div>
    );
  }
  if (!data) return null;

  const readyNow  = data.plan.filter((p) => p.ready && !p.inChamber).length;
  const dueOut    = data.chamber.filter((c) => c.dueOut).length;
  const visibleMoves = showAll ? data.moves : data.moves.slice(0, 8);
  const selectedTray = selected ? data.plan.find((p) => p.trayKey === selected) : null;

  if (walking) {
    return (
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <MoveWalkthrough
          data={data}
          steps={steps}
          entryByKey={entryByKey}
          onApplied={onApplied}
          onClose={(changed) => { setWalking(false); if (changed) load(); }}
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1460, margin: '0 auto' }}>
      {/* ── What this is ── */}
      <div style={{
        background: 'var(--card, #fff)', border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 14,
      }}>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
          Keeps each crop in one block, and puts whatever is closest to harvest in the
          easiest-to-reach slots. Benches 1–4 and 7 are walk-around so every slot counts
          as easy; on benches 5–6 slot 1 is the near end. Timings come from your own
          harvest records. Once the moves are done it stays settled: new ones only
          appear when the farm changes, such as a harvest, a tray coming out of the
          chamber, or crops coming ready.
        </div>
      </div>

      {/* ── At a glance ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <ArrangeStat label="Ready to cut" value={readyNow} colour={readyNow ? '#10b981' : undefined} />
        <ArrangeStat label="Moves suggested" value={data.moves.length} colour={data.moves.length ? '#f59e0b' : '#10b981'} />
        <ArrangeStat label="Due out of chamber" value={dueOut} colour={dueOut ? '#7c3aed' : undefined} />
        <ArrangeStat label="Free slots" value={data.capacity.freeFullSlots} />
      </div>

      {/* ── Warnings ── */}
      {data.warnings.map((w, i) => (
        <div key={i} style={{
          background: w.type === 'capacity' ? '#fef2f2' : '#fffbeb',
          border: `1px solid ${w.type === 'capacity' ? '#fecaca' : '#fde68a'}`,
          color: w.type === 'capacity' ? '#991b1b' : '#92400e',
          borderRadius: 'var(--r-md)', padding: '9px 12px', marginBottom: 8,
          fontSize: 13, lineHeight: 1.45,
        }}>
          {w.message}
        </div>
      ))}

      {/* ── Chamber switch ── */}
      <label style={{
        display: 'flex', alignItems: 'center', gap: 12, cursor: loading ? 'wait' : 'pointer',
        background: 'var(--card, #fff)', border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 'var(--r-md)', padding: '10px 14px', marginBottom: 14,
      }}>
        <input
          type="checkbox"
          checked={includeChamber}
          disabled={loading}
          onChange={(e) => toggleChamber(e.target.checked)}
          style={{ width: 20, height: 20, flex: '0 0 auto', accentColor: 'var(--brand-2, #2e7d32)' }}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>
            Take trays out of the germination chamber
          </span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
            {includeChamber
              ? 'On: trays due out by date are included in the moves.'
              : 'Off: chamber trays stay put and just keep a space. Use this when they aren’t ready yet.'}
          </span>
        </span>
      </label>

      {data.moves.length > 0 && (
        <button
          type="button"
          className="btn-tab active"
          onClick={() => { setSelected(null); setWalking(true); }}
          style={{ width: '100%', padding: '12px 14px', fontSize: 15, fontWeight: 800, marginBottom: 14 }}
        >
          ▶ Walk me through the {data.moves.length} moves
        </button>
      )}

      {/* ── Map ── */}
      <div style={{
        background: 'var(--card, #fff)', border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 14,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, flexWrap: 'wrap', marginBottom: 10,
        }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>
            The farm{' '}
            <span style={{ color: 'var(--muted)', fontWeight: 500 }}>
              · {view === 'now' ? 'as it is now' : 'as suggested'}
            </span>
          </h3>
          <div style={{ display: 'flex', gap: 0, border: '1px solid var(--line, #e5e7eb)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
            {['now', 'after'].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                style={{
                  border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  padding: '6px 14px',
                  background: view === v ? 'var(--brand-2, #2e7d32)' : 'transparent',
                  color: view === v ? '#fff' : 'var(--muted)',
                }}
              >
                {v === 'now' ? 'Now' : 'After'}
              </button>
            ))}
          </div>
        </div>

        <FarmMap
          benches={data.benches}
          state={view === 'now' ? steps.states[0] : steps.states[steps.states.length - 1]}
          entryByKey={entryByKey}
          view={view}
          selected={selected}
          onSelect={setSelected}
        />

        <div style={{ marginTop: 10 }}><MapLegend /></div>

        {selectedTray && (
          <div style={{
            marginTop: 10, padding: '9px 12px', borderRadius: 'var(--r-md)',
            background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 13,
          }}>
            <strong>{selectedTray.cropType}</strong>
            {selectedTray.size === 'half' && <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 6 }}>HALF</span>}
            <span style={{ color: daysColour(selectedTray.daysToGo), fontWeight: 700, marginLeft: 8 }}>
              {daysLabel(selectedTray.daysToGo)}
            </span>
            <div style={{ marginTop: 3, color: 'var(--muted)' }}>
              {selectedTray.currentLabel} → <strong style={{ color: 'var(--ink, #111)' }}>{selectedTray.targetLabel || 'nowhere to put it'}</strong>
              {selectedTray.inPlace && ' · already there'}
            </div>
            <div style={{ marginTop: 3, fontSize: 12, color: 'var(--muted)' }}>
              Seeded {selectedTray.seededAt || '—'} · day {selectedTray.ageDays ?? '—'} of ~{selectedTray.maturityDays}
              {selectedTray.maturityEstimated
                ? ' (estimated — no harvest history yet)'
                : ' (from your harvest records)'}
            </div>
          </div>
        )}
      </div>

      {/* ── Jobs ── */}
      <div style={{
        background: 'var(--card, #fff)', border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 'var(--r-md)', padding: '12px 14px', margin: '14px 0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>
            Moves {data.moves.length > 0 && <span style={{ color: 'var(--muted)', fontWeight: 500 }}>· in this order</span>}
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-tab" style={{ fontSize: 12, padding: '5px 10px' }} onClick={load} disabled={!!busy}>
              Recheck
            </button>
            {data.moves.length > 0 && (
              <button
                className="btn-tab"
                style={{ fontSize: 12, padding: '5px 10px' }}
                onClick={applyAll}
                disabled={!!busy}
              >
                {busy === 'all' ? 'Moving…' : 'Do all'}
              </button>
            )}
          </div>
        </div>

        {data.moves.length === 0 ? (
          <div style={{ padding: '18px 0', color: '#10b981', fontWeight: 600, fontSize: 14 }}>
            ✓ Nothing to move — the farm is settled.
          </div>
        ) : (
          <>
            <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
              {visibleMoves.map((m) => (
                <MoveRow
                  key={`${m.step}-${m.trayKey}`}
                  move={m}
                  onApply={applyMove}
                  busy={busy === m.step}
                  selected={selected === m.trayKey}
                  onSelect={() => setSelected(selected === m.trayKey ? null : m.trayKey)}
                />
              ))}
            </ul>
            {data.moves.length > 8 && (
              <button
                className="btn-tab"
                style={{ fontSize: 12, padding: '5px 10px', marginTop: 10 }}
                onClick={() => setShowAll((v) => !v)}
              >
                {showAll ? 'Show fewer' : `Show all ${data.moves.length}`}
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Germination chamber ── */}
      <div style={{
        background: 'var(--card, #fff)', border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 14,
      }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>
          Germination chamber <span style={{ color: 'var(--muted)', fontWeight: 500 }}>· where each tray goes</span>
        </h3>

        {data.chamber.length === 0 ? (
          <div style={{ padding: '14px 0', color: 'var(--muted)', fontSize: 13 }}>Chamber is empty.</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {data.chamber.map((c) => (
              <li key={c.trayId} style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                padding: '9px 0', borderTop: '1px solid var(--line, #e5e7eb)',
              }}>
                <span style={{
                  flex: '0 0 auto', fontSize: 11, fontWeight: 700, padding: '2px 7px',
                  borderRadius: 999,
                  background: c.dueOut ? '#dcfce7' : 'var(--chip, #f3f4f6)',
                  color: c.dueOut ? '#166534' : 'var(--muted)',
                }}>
                  {c.dueOut ? 'Due out' : `${c.daysUntilOut}d to go`}
                </span>

                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {c.cropType}
                    {c.size === 'half' && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', marginLeft: 6 }}>HALF</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {c.chamberLocation ? `${c.chamberLocation} · ` : ''}
                    day {c.daysInChamber} of {c.germDays}
                    {c.maturityEstimated && ' · timing estimated'}
                  </div>
                </div>

                <div style={{ flex: '0 0 auto', fontSize: 13, textAlign: 'right' }}>
                  {/* Trays not due out aren't in the moves yet; their slot is just kept free. */}
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    {c.leavesChamber ? 'goes to' : 'space kept at'}
                  </div>
                  {c.targetLabel
                    ? <strong>{c.targetLabel}</strong>
                    : <span style={{ color: '#b91c1c' }}>no room</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Crop blocks ── */}
      <div style={{
        background: 'var(--card, #fff)', border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 24,
      }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>
          Crops <span style={{ color: 'var(--muted)', fontWeight: 500 }}>· soonest first</span>
        </h3>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {data.groups.map((g) => (
            <li key={g.cropType} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '7px 0', borderTop: '1px solid var(--line, #e5e7eb)', fontSize: 13,
            }}>
              <span style={{ flex: 1, fontWeight: 600 }}>{g.cropType}</span>
              <span style={{ color: 'var(--muted)' }}>{g.trays} tray{g.trays === 1 ? '' : 's'}</span>
              <span style={{ flex: '0 0 90px', textAlign: 'right', fontWeight: 700, color: daysColour(g.soonestDaysToGo) }}>
                {daysLabel(g.soonestDaysToGo)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--muted)', paddingBottom: 20 }}>
        Worked out {new Date(data.generatedAt).toLocaleString()} · {data.capacity.traysOnBench} trays on benches,
        {' '}{data.capacity.traysInChamber} in the chamber
      </div>
    </div>
  );
}

// ── Harvest forecast: what will be ready, in blocks of days ──────────────────
const FORECAST_COLOURS = ['#10b981', '#f59e0b', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#64748b'];
const forecastColour = (i, count) => (i === count - 1 ? '#94a3b8' : FORECAST_COLOURS[Math.min(i, FORECAST_COLOURS.length - 2)]);
const fmtAmount = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const shortDate = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const addDays = (n) => new Date(Date.now() + n * 86400000);

function TrayPips({ amount, colour, max = 12 }) {
  if (!amount) return null;
  const shown = Math.min(amount, max);
  const full = Math.floor(shown);
  const half = shown - full >= 0.5;
  const pip = { width: 9, height: 9, borderRadius: 2, background: colour };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, maxWidth: 66 }}>
      {Array.from({ length: full }, (_, i) => <span key={i} style={pip} />)}
      {half && <span style={{ ...pip, width: 4 }} />}
      {amount > max && <span style={{ fontSize: 9, lineHeight: '9px', color: colour, fontWeight: 800 }}>+</span>}
    </div>
  );
}

function HarvestForecastView({ allTrays, onTrayClick }) {
  const [blockDays, setBlockDays]   = useState(7);
  const [cumulative, setCumulative] = useState(false);
  const [selected, setSelected]     = useState(null);   // { crop, col }

  const blocks = blockDays === 7 ? 4 : 6;
  const forecast = useMemo(
    () => buildHarvestForecast(allTrays, { blockDays, blocks }),
    [allTrays, blockDays, blocks]
  );
  const { columns, crops, totals, repeat } = forecast;

  const colLabel = (c, i) => {
    if (c.key === 'now') return { title: 'Ready now', sub: 'today' };
    if (c.key === 'later') return { title: 'Later', sub: `from ${shortDate(addDays(c.fromDay))}` };
    return {
      title: blockDays === 7 ? (i === 1 ? 'This week' : `Week ${i}`) : `Days ${c.fromDay}–${c.toDay}`,
      sub: `${shortDate(addDays(c.fromDay))} – ${shortDate(addDays(c.toDay))}`,
    };
  };

  const chipStyle = (active) => ({
    padding: '5px 13px', borderRadius: 'var(--r-lg)', border: '1px solid var(--border, #d4e0d4)',
    background: active ? 'var(--brand, #113449)' : 'var(--panel, #fff)',
    color: active ? '#fff' : 'var(--text, #111a14)', fontWeight: 700, fontSize: 13, cursor: 'pointer', margin: 0,
  });

  const selCrop = selected && crops.find((c) => c.cropName === selected.crop);
  // In running-total mode a cell covers every column up to and including it.
  const selRows = selCrop
    ? (cumulative ? selCrop.cells.slice(0, selected.col + 1) : [selCrop.cells[selected.col]]).flatMap((cell) => cell.rows)
    : [];

  if (!crops.length && !repeat.length) return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted, #5e7265)' }}>Nothing growing to forecast.</div>
  );

  const th = { padding: '8px 6px', textAlign: 'left', fontSize: 12, borderBottom: '1px solid var(--border, #d4e0d4)', whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginRight: 4 }}>Blocks:</span>
        <button style={chipStyle(blockDays === 7)} onClick={() => { setBlockDays(7); setSelected(null); }}>Weeks</button>
        <button style={chipStyle(blockDays === 3)} onClick={() => { setBlockDays(3); setSelected(null); }}>3 days</button>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginLeft: 8, marginRight: 4 }}>Show:</span>
        <button style={chipStyle(!cumulative)} onClick={() => setCumulative(false)}>Ready in that block</button>
        <button style={chipStyle(cumulative)} onClick={() => setCumulative(true)}>Running total</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
        {cumulative
          ? 'How many trays will have reached their harvest target by the end of each block, assuming nothing is cut in between.'
          : 'How many trays reach their harvest target in each block.'}
        {' '}Each square is a tray; a thin one is a half tray. Click a cell to see the trays.
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--border, #d4e0d4)', borderRadius: 'var(--r-lg)', background: 'var(--panel, #fff)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 120 + columns.length * 88 }}>
          <thead>
            <tr>
              <th style={{ ...th, paddingLeft: 12 }}>Crop</th>
              {columns.map((c, i) => {
                const { title, sub } = colLabel(c, i);
                return (
                  <th key={c.key} style={{ ...th, borderTop: `3px solid ${forecastColour(i, columns.length)}` }}>
                    <div style={{ fontWeight: 800 }}>{title}</div>
                    <div style={{ fontWeight: 500, fontSize: 11, color: 'var(--muted)' }}>{sub}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {crops.map((crop) => (
              <tr key={crop.cropName}>
                <td style={{ padding: '8px 6px 8px 12px', fontWeight: 700, fontSize: 13, borderBottom: '1px solid var(--border, #d4e0d4)', whiteSpace: 'nowrap' }}>
                  {crop.cropName}
                  <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--muted)' }}>{fmtAmount(crop.total)} growing</div>
                </td>
                {crop.cells.map((cell, i) => {
                  const amount = cumulative ? crop.cumulative[i] : cell.amount;
                  const colour = forecastColour(i, columns.length);
                  const isSel = selected?.crop === crop.cropName && selected?.col === i;
                  return (
                    <td
                      key={i}
                      onClick={() => amount && setSelected(isSel ? null : { crop: crop.cropName, col: i })}
                      style={{
                        padding: '8px 6px', verticalAlign: 'top', borderBottom: '1px solid var(--border, #d4e0d4)',
                        cursor: amount ? 'pointer' : 'default',
                        background: isSel ? `${colour}22` : amount && !cumulative ? `${colour}0d` : 'transparent',
                        outline: isSel ? `2px solid ${colour}` : 'none', outlineOffset: -2,
                      }}
                    >
                      {amount ? (
                        <>
                          <div style={{ fontSize: 14, fontWeight: 800, color: colour, marginBottom: 3 }}>{fmtAmount(amount)}</div>
                          <TrayPips amount={amount} colour={colour} />
                        </>
                      ) : <span style={{ color: 'var(--border, #d4e0d4)' }}>·</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr style={{ background: 'var(--panel-2, #eef2ee)' }}>
              <td style={{ padding: '8px 6px 8px 12px', fontWeight: 800, fontSize: 13 }}>All crops</td>
              {totals.map((t, i) => {
                const amount = cumulative ? totals.slice(0, i + 1).reduce((a, b) => a + b, 0) : t;
                return (
                  <td key={i} style={{ padding: '8px 6px', fontWeight: 800, fontSize: 14, color: amount ? 'var(--text)' : 'var(--muted)' }}>
                    {fmtAmount(amount)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {repeat.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
          <b style={{ color: '#10b981' }}>✂ Cut &amp; come again</b> (always available, not forecast):{' '}
          {repeat.map((r) => `${r.cropName} ×${fmtAmount(r.amount)}`).join(', ')}
        </div>
      )}

      {selCrop && (
        <div style={{ marginTop: 14, border: '1px solid var(--border, #d4e0d4)', borderRadius: 'var(--r-lg)', background: 'var(--panel, #fff)', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', fontWeight: 800, fontSize: 14, borderBottom: '1px solid var(--border, #d4e0d4)', display: 'flex', justifyContent: 'space-between' }}>
            <span>{selCrop.cropName} · {cumulative ? 'ready by end of' : ''} {colLabel(columns[selected.col], selected.col).title.toLowerCase()}</span>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 14 }}>✕</button>
          </div>
          {selRows.map((row) => {
            const { benchName, layerIndex, trayIndex, half, tray, ageDays, daysLeft, stage } = row;
            return (
              <div
                key={`${benchName}-${layerIndex}-${trayIndex}-${half ?? 'f'}`}
                role="button" tabIndex={0}
                onClick={() => onTrayClick(benchName, layerIndex, trayIndex, half)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onTrayClick(benchName, layerIndex, trayIndex, half); }}
                style={{
                  display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                  padding: '8px 14px', borderBottom: '1px solid var(--border, #d4e0d4)', cursor: 'pointer', fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 600, minWidth: 170 }}>
                  {formatBenchName(benchName)} · L{layerIndex + 1} T{trayIndex + 1}{half != null ? `·${halfLabel(half)} (½)` : ''}
                </span>
                <StagePill stage={stage} />
                <span style={{ color: 'var(--muted)' }}>{ageDays}d old{tray.seededAt ? ` · seeded ${tray.seededAt}` : ''}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 700, color: daysLeft === 0 ? '#10b981' : 'var(--text)' }}>
                  {daysLeft === 0 ? 'Ready' : `Ready ${shortDate(addDays(daysLeft))} (${daysLeft}d)`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SummaryView({ layout, onTrayClick, seedInventory }) {
  const [filterStage, setFilterStage] = useState('all');
  const [filterBench, setFilterBench] = useState('all');
  const [activeView,  setActiveView]  = useState('all');   // 'all' | 'ready' | 'forecast' | 'boxes'
  const [sortBy,      setSortBy]      = useState('readiness');
 
  const allTrays = useMemo(() => {
    if (!layout?.benches) return [];
    const rows = [];
    Object.entries(layout.benches).forEach(([benchName, bench]) => {
      bench.layers.forEach((layer, layerIndex) => {
        layer.forEach((slot, trayIndex) => {
          // A slot is either one full tray or a split container of two halves
          const entries = isSplitSlot(slot)
            ? (slot.halves || []).map((h, hi) => ({ tray: h, half: hi })).filter((e) => e.tray?.cropType)
            : (slot?.cropType ? [{ tray: slot, half: null }] : []);

          entries.forEach(({ tray, half }) => {
            const ageDays = tray.seededAt ? (daysSince(tray.seededAt) ?? 0) : 0;
            const { type: harvestType, targetDays } = resolveHarvestInfo(tray.cropType, seedInventory);
            const pct = targetDays ? Math.min(100, Math.round((ageDays / targetDays) * 100)) : null;
            rows.push({
              benchName, layerIndex, trayIndex, half, tray,
              ageDays, stage: (tray.stage || 'direct').toLowerCase(),
              harvestType, targetDays, pct,
            });
          });
        });
      });
    });
    return rows;
  }, [layout, seedInventory]);

  const benchNames = useMemo(() => {
    if (!layout?.benches) return [];
    return Object.keys(layout.benches).filter((b) =>
      layout.benches[b].layers.some((l) => l.some((t) => isSlotOccupied(t)))
    );
  }, [layout]);
 
  const filteredAll = useMemo(() => {
    let rows = [...allTrays];
    if (filterStage !== 'all') rows = rows.filter((r) => r.stage === filterStage);
    if (filterBench !== 'all') rows = rows.filter((r) => r.benchName === filterBench);
    if (sortBy === 'readiness') rows.sort((a, b) => {
      if (a.harvestType === 'repeat' && b.harvestType !== 'repeat') return -1;
      if (b.harvestType === 'repeat' && a.harvestType !== 'repeat') return 1;
      return (b.pct ?? 0) - (a.pct ?? 0);
    });
    if (sortBy === 'age')   rows.sort((a, b) => b.ageDays - a.ageDays);
    if (sortBy === 'name')  rows.sort((a, b) => a.tray.cropType.localeCompare(b.tray.cropType));
    if (sortBy === 'bench') rows.sort((a, b) => a.benchName.localeCompare(b.benchName) || b.ageDays - a.ageDays);
    return rows;
  }, [allTrays, filterStage, filterBench, sortBy]);
 
  const readyRows = useMemo(
    () => allTrays.filter((r) => r.harvestType === 'repeat' || (r.pct !== null && r.pct >= 100)),
    [allTrays]
  );
 
  const readyNow  = allTrays.filter((r) => r.harvestType !== 'repeat' && r.pct >= 100).length;
  const readySoon = allTrays.filter((r) => r.harvestType !== 'repeat' && r.pct !== null && r.pct >= 75 && r.pct < 100).length;
  const repeatCt  = allTrays.filter((r) => r.harvestType === 'repeat').length;
  const stageColors = { starter: '#06b6d4', direct: '#f59e0b', transplanted: '#10b981' };
 
  const chipStyle = (active, color) => ({
    padding: '5px 13px', borderRadius: 'var(--r-lg)', border: '1px solid var(--border, #d4e0d4)',
    background: active ? (color || 'var(--brand, #113449)') : 'var(--panel, #fff)',
    color: active ? '#fff' : 'var(--text, #111a14)',
    fontWeight: 700, fontSize: 13, cursor: 'pointer', margin: 0,
    boxShadow: active && color ? 'var(--shadow)' : 'none',
    transition: 'background .15s',
  });
 
  if (allTrays.length === 0) return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted, #5e7265)' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🌱</div>
      <div style={{ fontWeight: 700, fontSize: 18 }}>No trays seeded yet</div>
      <div style={{ fontSize: 14, marginTop: 6 }}>Head to the Layout tab to start seeding.</div>
    </div>
  );
 
  return (
    <div style={{ padding: '0 4px' }}>
 
      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total trays',    value: allTrays.length, color: 'var(--brand-2, #2e7d32)' },
          { label: '✓ Ready now',    value: readyNow,        color: readyNow  > 0 ? '#10b981' : 'var(--muted)' },
          { label: '⏳ Nearly ready', value: readySoon,       color: readySoon > 0 ? '#f59e0b' : 'var(--muted)' },
          { label: '✂ Repeat crops', value: repeatCt,        color: '#06b6d4' },
          { label: 'Oldest',         value: `${Math.max(...allTrays.map((t) => t.ageDays))}d`, color: '#e879a0' },
        ].map((s, i) => (
          <div key={i} style={{
            background: 'var(--panel, #fff)', border: '1px solid var(--border, #d4e0d4)',
            borderRadius: 'var(--r-lg)', padding: '10px 16px', minWidth: 100, flex: '1 1 auto',
            borderTop: `3px solid ${s.color}`,
            ...stagger(i, 'td-fade-up', 0, 40, 300),
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--muted, #5e7265)', fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>
 
      {/* View switcher */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={chipStyle(activeView === 'all')} onClick={() => setActiveView('all')}>
          📋 All trays
        </button>
        <button style={chipStyle(activeView === 'ready', '#10b981')} onClick={() => setActiveView('ready')}>
          ✓ Ready to harvest
          {(readyNow + repeatCt) > 0 && (
            <span style={{
              marginLeft: 6,
              background: activeView === 'ready' ? 'rgba(255,255,255,0.3)' : '#10b981',
              color: '#fff', borderRadius: 999, padding: '1px 7px', fontSize: 12,
            }}>{readyNow + repeatCt}</span>
          )}
        </button>
        <button style={chipStyle(activeView === 'forecast', '#3b82f6')} onClick={() => setActiveView('forecast')}>
          📅 Harvest forecast
        </button>
        <button style={chipStyle(activeView === 'boxes', 'var(--brand-2, #2e7d32)')} onClick={() => setActiveView('boxes')}>
          📦 Box readiness
        </button>
      </div>
 
      {/* ── All trays ── */}
      {activeView === 'all' && (<>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginRight: 4 }}>Stage:</span>
          {['all','starter','direct','transplanted'].map((s) => (
            <button key={s} style={chipStyle(filterStage === s)} onClick={() => setFilterStage(s)}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          {benchNames.length > 1 && <>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginLeft: 8, marginRight: 4 }}>Bench:</span>
            <button style={chipStyle(filterBench === 'all')} onClick={() => setFilterBench('all')}>All</button>
            {benchNames.map((b) => (
              <button key={b} style={chipStyle(filterBench === b)} onClick={() => setFilterBench(b)}>
                {formatBenchName(b)}
              </button>
            ))}
          </>}
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginLeft: 8, marginRight: 4 }}>Sort:</span>
          {[['readiness','Readiness'],['age','Oldest first'],['name','Crop name'],['bench','By bench']].map(([v, l]) => (
            <button key={v} style={chipStyle(sortBy === v)} onClick={() => setSortBy(v)}>{l}</button>
          ))}
        </div>
 
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12, fontWeight: 600 }}>
          {filteredAll.length} tray{filteredAll.length !== 1 ? 's' : ''}
        </div>
 
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filteredAll.map((row, i) => {
            const { benchName, layerIndex, trayIndex, half, tray, ageDays, stage, harvestType, pct } = row;
            const sc    = stageColors[stage] || '#999';
            const notes = (tray.notes || '').trim();
            const borderAccent =
              harvestType === 'repeat' ? '#10b981'
              : pct >= 100            ? '#10b981'
              : pct >= 75             ? '#f59e0b'
              :                         sc;

            return (
              <div
                key={`${benchName}-${layerIndex}-${trayIndex}-${half ?? 'f'}`}
                role="button" tabIndex={0}
                onClick={() => onTrayClick(benchName, layerIndex, trayIndex, half)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onTrayClick(benchName, layerIndex, trayIndex, half); }}
                className="tray-summary-item"
                style={{
                  background: 'var(--panel, #fff)',
                  border: '1px solid var(--border, #d4e0d4)',
                  borderLeft: `4px solid ${borderAccent}`,
                  borderRadius: 'var(--r-lg)', padding: '10px 14px', cursor: 'pointer',
                  transition: 'box-shadow .15s, transform .1s',
                  boxShadow: 'var(--shadow-card)',
                  ...stagger(i, 'td-fade-up', 0, 25, 300),
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; e.currentTarget.style.transform = 'none'; }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{tray.cropType}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted, #5e7265)', marginTop: 1 }}>
                    {benchName} · L{layerIndex + 1} T{trayIndex + 1}{half != null ? `·${halfLabel(half)} (½)` : ''}
                  </div>
                </div>
                <div><StagePill stage={stage} /></div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: sc, marginBottom: 3 }}>{ageDays}d old</div>
                  <div style={{ height: 5, borderRadius: 999, background: 'var(--panel-2, #eef2ee)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 999, background: sc, width: `${Math.min(100, (ageDays / 60) * 100)}%`, transition: 'width .5s ease' }} />
                  </div>
                </div>
                <ReadinessBadge ageDays={ageDays} cropName={tray.cropType} seedInventory={seedInventory} />
                <div style={{
                  fontSize: 12, color: 'var(--muted)', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160,
                  fontStyle: notes ? 'normal' : 'italic', opacity: notes ? 1 : .5,
                }}>
                  {notes || 'No notes'}
                </div>
              </div>
            );
          })}
        </div>
      </>)}
 
      {/* ── Ready grouped ── */}
      {activeView === 'ready' && (
        <ReadyGroupedView rows={readyRows} onTrayClick={onTrayClick} />
      )}
 
      {/* ── Harvest forecast ── */}
      {activeView === 'forecast' && (
        <HarvestForecastView allTrays={allTrays} onTrayClick={onTrayClick} />
      )}

      {/* ── Box readiness ── */}
      {activeView === 'boxes' && (
        <BoxReadinessView allTrays={allTrays} seedInventory={seedInventory} />
      )}
    </div>
  );
}

// ---------- Seed Inventory (localStorage-backed) ----------
const SEED_STORAGE_KEY = 'seedInventory_v1';

export function useSeedInventory() {
  const [seeds, setSeeds]       = useState({});   // { [name]: { amount, gramsPerTray, harvestDays, harvestType } }
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
 
  // Convert the array the API returns into the keyed-object shape the
  // rest of TrayManager already expects.
  const normalise = (arr) => {
    const map = {};
    (arr || []).forEach((s) => {
      map[s.name] = {
        amount:       s.amount       ?? 0,
        gramsPerTray: s.gramsPerTray ?? 0,
        harvestDays:  s.harvestDays  ?? null,
        harvestType:  s.harvestType  ?? null,
        unit: 'g',
      };
    });
    return map;
  };
 
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get('/seeds');
      setSeeds(normalise(res.data));
      setError(null);
    } catch (e) {
      console.error('useSeedInventory load failed', e);
      setError('Failed to load seed inventory');
    } finally {
      setLoading(false);
    }
  }, []);
 
  useEffect(() => { reload(); }, [reload]);
 
  // Add a new crop
  const addCrop = useCallback(async (name, gramsPerTray) => {
    await axiosClient.post('/seeds', { name, gramsPerTray, amount: 0 });
    await reload();
  }, [reload]);
 
  // Update any fields on a crop (amount, gramsPerTray, harvestDays, harvestType)
  const updateCrop = useCallback(async (name, fields) => {
    await axiosClient.patch(`/seeds/${encodeURIComponent(name)}`, fields);
    // Optimistic local update so inputs don't flicker
    setSeeds((prev) => ({
      ...prev,
      [name]: { ...prev[name], ...fields },
    }));
  }, []);
 
  // Delete a crop
  const removeCrop = useCallback(async (name) => {
    await axiosClient.delete(`/seeds/${encodeURIComponent(name)}`);
    setSeeds((prev) => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  }, []);
 
  return { seeds, setSeeds, loading, error, reload, addCrop, updateCrop, removeCrop };
}

// shape:
// {
//   "Basil": { amount: 120, unit: "g" },
//   "Sunflower": { amount: 5000, unit: "seeds" }
// }

export function SeedInventoryView({ seeds, addCrop, updateCrop, removeCrop, loading, error }) {
  const [name, setName]               = useState('');
  const [gramsPerTray, setGramsPerTray] = useState('');
  const [saving, setSaving]           = useState({});
  const [pendingFields, setPendingFields] = useState({}); // { [cropName]: { field: value } }
 
  const sortedSeeds = useMemo(
    () => Object.entries(seeds).sort(([a], [b]) => a.localeCompare(b)),
    [seeds]
  );
 
  // ── local pending edits (debounced patch) ──────────────────────────────────
  const setPending = (cropName, field, value) => {
    setPendingFields((prev) => ({
      ...prev,
      [cropName]: { ...(prev[cropName] || {}), [field]: value },
    }));
  };
 
  const flushPending = async (cropName) => {
    const fields = pendingFields[cropName];
    if (!fields || !Object.keys(fields).length) return;
    setSaving((s) => ({ ...s, [cropName]: true }));
    try {
      await updateCrop(cropName, fields);
      setPendingFields((prev) => { const c = { ...prev }; delete c[cropName]; return c; });
    } catch (e) {
      console.error('Save failed', e);
    } finally {
      setSaving((s) => { const c = { ...s }; delete c[cropName]; return c; });
    }
  };
 
  const handleAdd = async () => {
    if (!name.trim() || !gramsPerTray) return;
    try {
      await addCrop(name.trim(), parseFloat(gramsPerTray) || 0);
      setName('');
      setGramsPerTray('');
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to add crop');
    }
  };
 
  const handleRemove = async (cropName) => {
    if (!window.confirm(`Remove ${cropName} from inventory? This cannot be undone.`)) return;
    await removeCrop(cropName);
  };
 
  const HARVEST_TYPE_OPTS = [
    { value: '',         label: 'Auto-detect' },
    { value: 'standard', label: 'Standard (30d)' },
    { value: 'micro',    label: 'Micro (14d)' },
    { value: 'repeat',   label: 'Repeat harvest' },
  ];
 
  // Display value: pending edit takes priority, then saved value
  const getVal = (cropName, field) => {
    if (pendingFields[cropName]?.[field] !== undefined) return pendingFields[cropName][field];
    return seeds[cropName]?.[field] ?? '';
  };
 
  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>🌱 Seed Inventory</h3>
        {loading && <span style={{ fontSize: 12, color: 'var(--muted, #5e7265)' }}>Loading…</span>}
        {error   && <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>}
      </div>
 
      <div style={{ overflowX: 'auto' }}>
      {/* Column header hint */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 80px 80px 140px 100px 36px',
        gap: 10, marginBottom: 6, padding: '0 4px',
      }}>
        {['Crop name', 'In stock (g)', 'g / tray', 'Harvest type', 'Days to harvest', ''].map((h, i) => (
          <div key={i} style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted, #5e7265)' }}>{h}</div>
        ))}
      </div>
 
      {/* Add row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 80px 80px 140px 100px 36px',
        gap: 10, marginBottom: 20,
        padding: '12px', borderRadius: 'var(--r-lg)',
        background: 'var(--panel-2, #eef2ee)',
        border: '1px solid var(--border, #d4e0d4)',
      }}>
        <input
          className="seed-input"
          placeholder="Crop name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          style={{ padding: '7px 10px', borderRadius: 'var(--r-md)', border: '1px solid var(--border, #d4e0d4)', fontSize: 13 }}
        />
        <input
          className="seed-input"
          type="number" placeholder="0"
          style={{ padding: '7px 10px', borderRadius: 'var(--r-md)', border: '1px solid var(--border, #d4e0d4)', fontSize: 13 }}
          disabled
        />
        <input
          className="seed-input"
          type="number" placeholder="g/tray"
          value={gramsPerTray}
          onChange={(e) => setGramsPerTray(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          style={{ padding: '7px 10px', borderRadius: 'var(--r-md)', border: '1px solid var(--border, #d4e0d4)', fontSize: 13 }}
        />
        <div /> <div />
        <button
          onClick={handleAdd}
          disabled={!name.trim() || !gramsPerTray}
          style={{
            padding: '7px 0', borderRadius: 'var(--r-md)', border: 'none',
            background: 'var(--brand-2, #2e7d32)', color: '#fff', fontWeight: 700,
            fontSize: 18, cursor: 'pointer', lineHeight: 1,
          }}
        >+</button>
      </div>
 
      {/* Crop rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sortedSeeds.map(([cropName, val]) => {
          const isSaving = !!saving[cropName];
          const hasPending = !!(pendingFields[cropName] && Object.keys(pendingFields[cropName]).length);
 
          return (
            <div
              key={cropName}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 80px 80px 140px 100px 36px',
                gap: 10, alignItems: 'center',
                padding: '10px 12px',
                borderRadius: 'var(--r-lg)',
                background: 'var(--panel, #fff)',
                border: `1px solid ${hasPending ? '#f59e0b' : 'var(--border, #d4e0d4)'}`,
                transition: 'border-color .15s',
              }}
            >
              {/* Name */}
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                {cropName}
                {isSaving && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--muted)' }}>saving…</span>}
              </div>
 
              {/* Amount */}
              <input
                type="number"
                value={getVal(cropName, 'amount')}
                onChange={(e) => setPending(cropName, 'amount', parseFloat(e.target.value) || 0)}
                onBlur={() => flushPending(cropName)}
                style={{ padding: '5px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--border, #d4e0d4)', fontSize: 13, width: '100%' }}
              />
 
              {/* gramsPerTray */}
              <input
                type="number"
                value={getVal(cropName, 'gramsPerTray')}
                onChange={(e) => setPending(cropName, 'gramsPerTray', parseFloat(e.target.value) || 0)}
                onBlur={() => flushPending(cropName)}
                style={{ padding: '5px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--border, #d4e0d4)', fontSize: 13, width: '100%' }}
              />
 
              {/* harvestType */}
              <select
                value={getVal(cropName, 'harvestType') || ''}
                onChange={(e) => {
                  setPending(cropName, 'harvestType', e.target.value || null);
                  // Flush immediately on select change
                  setTimeout(() => flushPending(cropName), 0);
                }}
                style={{ padding: '5px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--border, #d4e0d4)', fontSize: 12, width: '100%' }}
              >
                {HARVEST_TYPE_OPTS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
 
              {/* harvestDays - only makes sense for standard/micro */}
              <input
                type="number"
                placeholder="default"
                value={getVal(cropName, 'harvestDays') ?? ''}
                onChange={(e) => {
                  const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                  setPending(cropName, 'harvestDays', v);
                }}
                onBlur={() => flushPending(cropName)}
                style={{ padding: '5px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--border, #d4e0d4)', fontSize: 13, width: '100%' }}
              />
 
              {/* Remove */}
              <button
                onClick={() => handleRemove(cropName)}
                title={`Remove ${cropName}`}
                style={{
                  width: 28, height: 28, borderRadius: 'var(--r-md)', border: '1px solid #fca5a5',
                  background: '#fff', color: '#ef4444', fontWeight: 700, fontSize: 15,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1,
                }}
              >×</button>
            </div>
          );
        })}
 
        {sortedSeeds.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted, #5e7265)', fontSize: 13 }}>
            No crops yet - add one above.
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function ChamberTrayRow({ tray, idx, onTrayClick, onMove, mode, selectedStarter, transplantTargets }) {
  const trayId = tray.id || tray._id;
  const [locationDraft, setLocationDraft] = useState(tray.chamberLocation || '');

  useEffect(() => {
    setLocationDraft(tray.chamberLocation || '');
  }, [tray.chamberLocation]);

  const saveLocation = useCallback(async (val) => {
    try {
      await axiosClient.post('/bench-layout/chamber-location', { trayId, location: val });
    } catch (e) {
      console.error('Failed to save chamber location', e);
    }
  }, [trayId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <Tray
        benchName="germinationChamber"
        layerIndex={0}
        trayIndex={idx}
        trayData={tray}
        mode={mode}
        selectedStarter={selectedStarter}
        transplantTargets={transplantTargets}
        handleTrayClick={() => onTrayClick({ bench: 'germinationChamber', trayId })}
        onMove={onMove}
      />
      <input
        type="text"
        placeholder="Location..."
        value={locationDraft}
        onChange={(e) => setLocationDraft(e.target.value)}
        onBlur={(e) => saveLocation(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        style={{
          width: 126,
          padding: '3px 6px',
          borderRadius: 'var(--r-md)',
          border: `1px solid ${locationDraft ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)'}`,
          fontSize: 11,
          textAlign: 'center',
          background: locationDraft ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.55)',
          color: 'var(--text)',
          outline: 'none',
          boxSizing: 'border-box',
          transition: 'background .15s, border-color .15s',
        }}
      />
    </div>
  );
}

function GerminationChamber({
  chamberTrays,
  onTrayClick,
  onMove,
  mode,
  selectedStarter,
  transplantTargets,
  handleOpenGerminationSeed,
  compact = false,
}) {

  return (
    <div
      className={compact ? 'chamber-compact' : undefined}
      style={{
        background: 'var(--panel, #fff)',
        borderRadius: 'var(--r-xl)',
        padding: 20,
        border: '1px solid var(--border, #d4e0d4)',
      }}
    >

      {/* Header */}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18,
        }}
      >
        <h2 style={{ margin: 0 }}>
          Germination Chamber
        </h2>
      </div>

      {/* Add tray button */}

      <button
        onClick={handleOpenGerminationSeed}
        style={{
          marginBottom: 18,
          padding: '10px 16px',
          borderRadius: 'var(--r-md)',
          border: 'none',
          background: 'var(--brand-2, #2e7d32)',
          color: '#fff',
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        + Seed Into Chamber
      </button>

      {/* Tray grid */}

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        minHeight: 100,
        padding: 10,
        background: 'rgba(255,255,255,0.5)',
        borderRadius: 'var(--r-lg)',
        border: '1px dashed var(--border)'
      }}>
        {chamberTrays.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 14, margin: 'auto' }}>
            No trays currently in germination
          </div>
        ) : (
          (chamberTrays || []).map((tray, idx) => (
            <ChamberTrayRow
              key={tray.id || tray._id || idx}
              tray={tray}
              idx={idx}
              onTrayClick={onTrayClick}
              onMove={onMove}
              mode={mode}
              selectedStarter={selectedStarter}
              transplantTargets={transplantTargets}
            />
          ))
        )}
      </div>
    </div>
  );
}
// ---------- Slot cell ----------
// One bench slot: a full tray, or the two halves of a split slot.
function SlotCell({ benchName, layerIndex, trayIndex, slot, trayProps }) {
  const common = { benchName, layerIndex, trayIndex, ...trayProps };
  if (!isSplitSlot(slot)) return <Tray trayData={slot} {...common} />;
  return (
    <div className="tray-split">
      {[0, 1].map((h) => (
        <Tray key={h} halfIndex={h} trayData={slot.halves?.[h] || null} {...common} />
      ))}
    </div>
  );
}

// ---------- Room view ----------
// The Layout tab drawn the way the grow room is laid out: benches 1-3 down
// the left wall, the aisle, then benches 4-7 on the right with slot 1 at
// the aisle. Top of the screen is the far wall, so the lowest-numbered
// bench on each side is nearest the door. Layer 1 is the bottom shelf.
const ROOM_LEFT  = ['bench3', 'bench2', 'bench1'];
const ROOM_RIGHT = ['bench7', 'bench6', 'bench5', 'bench4'];

function RoomBench({ benchName, bench, trayProps, index }) {
  const used  = bench.layers.reduce((n, l) => n + l.filter((t) => isSlotOccupied(t)).length, 0);
  const total = bench.layers.reduce((n, l) => n + l.length, 0);

  // Name and count sit in a gutter beside the shelves, not above them,
  // so a bench is exactly as tall as its layers.
  return (
    <div className="room-bench" style={stagger(index, 'td-fade-up', 20, 50, 300)}>
      <div className="room-bench-name" title={formatBenchName(benchName)}>
        <span>Bench</span>
        <strong>{benchName.replace(/\D/g, '')}</strong>
        <span style={{ color: used > 0 ? 'var(--brand-2, #2e7d32)' : undefined }}>{used}/{total}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {[...bench.layers].reverse().map((layer, reversedIndex) => {
          const layerIndex = bench.layers.length - 1 - reversedIndex;
          return (
            <div key={layerIndex} className="bench-layer">
              {layer.map((slot, trayIndex) => (
                <SlotCell
                  key={trayIndex} benchName={benchName} layerIndex={layerIndex}
                  trayIndex={trayIndex} slot={slot} trayProps={trayProps}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RoomView({ layout, trayProps }) {
  const side = (names) => names.filter((n) => layout.benches[n]).map((benchName, i) => (
    <RoomBench
      key={benchName} benchName={benchName} bench={layout.benches[benchName]}
      trayProps={trayProps} index={i}
    />
  ));

  return (
    <div className="room-view">
      <div className="room-side room-side--fill">{side(ROOM_LEFT)}</div>
      <div className="room-aisle" aria-hidden="true" />
      <div className="room-side">{side(ROOM_RIGHT)}</div>
    </div>
  );
}

// ---------- Main ----------
const TrayManager = () => {
  const [layout, setLayout]                         = useState(null);
  const [activeTab, setActiveTab]                   = useState('layout'); // 'layout' | 'summary' | 'arrange' | 'seeds'
  const [selectedStarter, setSelectedStarter]       = useState(null);
  const [transplantTargets, setTransplantTargets]   = useState([]);
  const [mode, setMode]                             = useState('idle');
  const [menuTray, setMenuTray]                     = useState(null);
  const [awaitingTransplantConfirm, setAwaitingTransplantConfirm] = useState(false);
  const [seedPopup, setSeedPopup]                   = useState(null);
  const [selectedCrop, setSelectedCrop]             = useState('');
  const [seedWeight, setSeedWeight]                 = useState('');
  const [seedMode, setSeedMode]                     = useState('weight');
  const [seedCount, setSeedCount]                   = useState('');
  const [seedSize, setSeedSize]                     = useState('full'); // 'full' | 'half'
  const [seedQty, setSeedQty]                       = useState('1');    // chamber only: duplicate trays
  const [seedCategory, setSeedCategory]             = useState('all');
  const [expandedBenches, setExpandedBenches]       = useState({});
  const [layoutView, setLayoutView]                 = useState(() => {
    try { return localStorage.getItem('layout.view') === 'grid' ? 'grid' : 'room'; } catch { return 'room'; }
  });
  const [appendMode, setAppendMode]                 = useState(false);
  const [appendText, setAppendText]                 = useState('');

  const { seeds: seedInventory, loading: seedsLoading, error: seedsError, addCrop, updateCrop, removeCrop, reload: reloadSeeds } = useSeedInventory();

  const { settings: clientSettings, cropList, gramsFor, categoryFor, seedModeFor, seedsPerTrayFor } =
    useClientSettings();

  const setBenchExpanded = (name, v = true) => setExpandedBenches((s) => ({ ...s, [name]: v }));


  const fetchLayout = useCallback(async () => {
    try {
      const res = await axiosClient.get('/bench-layout');
      setLayout(res.data);
    } catch (err) {
      console.error('Failed to fetch layout', err);
    }
  }, []);

  useEffect(() => { fetchLayout(); }, [fetchLayout]);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setMenuTray(null);
        setSeedPopup(null);
        setAwaitingTransplantConfirm(false);
        setMode('idle');
        setAppendMode(false);
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

const cropOptions = useMemo(() => {
  // Pull keys directly from your new Inventory state
  return Object.keys(seedInventory).sort();
}, [seedInventory]);

const handleTrayClick = useCallback((location) => {
  if (!layout) return;

  const isChamber =
    location.bench === 'germinationChamber';

  let tray = null;

  // ─────────────────────────────────────────────
  // Germination chamber tray lookup
  // ─────────────────────────────────────────────

  if (isChamber) {
    tray = layout.germinationChamber?.trays?.find(
      (t) => t.id === location.trayId
    );
  }

  // ─────────────────────────────────────────────
  // Standard bench lookup
  // ─────────────────────────────────────────────

  else {
    const slot =
      layout.benches?.[location.bench]?.layers?.[
        location.layer
      ]?.[location.tray];

    tray = isSplitSlot(slot)
      ? (location.half != null ? slot.halves?.[location.half] : null)
      : slot;
  }

  // ─────────────────────────────────────────────
  // TRANSPLANT MODE
  // ─────────────────────────────────────────────

  if (mode === 'transplanting') {

    // Chamber trays cannot be transplant targets
    // because it's effectively infinite/unstructured.
    // Only physical bench slots should be targets.

    if (isChamber) {
      return;
    }

    setTransplantTargets((prev) => {
      const sameLoc = (t) =>
        t.bench === location.bench &&
        t.layer === location.layer &&
        t.tray === location.tray &&
        (t.half ?? null) === (location.half ?? null);

      if (prev.some(sameLoc)) {
        return prev.filter((t) => !sameLoc(t));
      }

      return [
        ...prev,
        {
          bench: location.bench,
          layer: location.layer,
          tray: location.tray,
          ...(location.half != null ? { half: location.half } : {}),
        },
      ];
    });

    return;
  }

  // ─────────────────────────────────────────────
  // EMPTY SLOT CLICKED
  // ─────────────────────────────────────────────

  if (!tray || !tray.cropType) {

    // Germination chamber doesn't seed into slots.
    // It appends dynamically.

    if (isChamber) {
      setSeedPopup({
        bench: 'germinationChamber',
      });
    }

    // Standard benches still use coordinates

    else {
      setSeedPopup({
        bench: location.bench,
        layer: location.layer,
        tray: location.tray,
        // Clicking the empty half of a split cell locks the popup to half size
        ...(location.half != null ? { half: location.half } : {}),
      });
    }

    setSelectedCrop('');
    setSeedWeight('');
    setSeedCategory('all');
    setSeedSize(location.half != null ? 'half' : 'full');
    setSeedQty('1');

    return;
  }

  // ─────────────────────────────────────────────
  // EXISTING TRAY CLICKED
  // ─────────────────────────────────────────────

  if (mode === 'idle') {

    // Germination chamber tray

    if (isChamber) {
      setMenuTray({
        bench: 'germinationChamber',
        trayId: tray.id,
        weight: '',
        trayData: tray,
      });
    }

    // Standard bench tray

    else {
      setMenuTray({
        bench: location.bench,
        layer: location.layer,
        tray: location.tray,
        ...(location.half != null ? { half: location.half } : {}),
        weight: '',
        trayData: tray,
      });
    }

    setActiveTab('layout');
  }
}, [layout, mode]);

const handleSeed = async (
  bench,
  layer,
  tray,
  cropTypeParam,
  half
) => {
  try {

    const sizeFields =
      seedSize === 'half'
        ? { traySize: 'half', ...(half != null ? { half } : {}) }
        : {};

    const quantity = Math.max(1, parseInt(seedQty, 10) || 1);

    // ─────────────────────────────────────────
    // Weight mode
    // ─────────────────────────────────────────

    if (seedMode === 'weight') {

      const grams = parseFloat(seedWeight);

      if (
        !cropTypeParam ||
        isNaN(grams) ||
        grams <= 0
      ) {
        return;
      }

      // Germination chamber
      if (bench === 'germinationChamber') {

        await axiosClient.post('/bench-layout/seed', {
          bench: 'germinationChamber',
          cropType: cropTypeParam,
          seedWeight: grams,
          seedUnit: 'g',
          quantity,
          ...sizeFields,
        });
      }

      // Standard benches
      else {

        await axiosClient.post('/bench-layout/seed', {
          bench,
          layer,
          tray,
          cropType: cropTypeParam,
          seedWeight: grams,
          seedUnit: 'g',
          ...sizeFields,
        });
      }
    }

    // ─────────────────────────────────────────
    // Seed-count mode
    // ─────────────────────────────────────────

    else {

      const count = parseInt(seedCount, 10);

      if (
        !cropTypeParam ||
        !Number.isFinite(count) ||
        count <= 0
      ) {
        return;
      }

      // Germination chamber
      if (bench === 'germinationChamber') {

        await axiosClient.post('/bench-layout/seed', {
          bench: 'germinationChamber',
          cropType: cropTypeParam,
          seedCount: count,
          seedUnit: 'seeds',
          quantity,
          ...sizeFields,
        });
      }

      // Standard benches
      else {

        await axiosClient.post('/bench-layout/seed', {
          bench,
          layer,
          tray,
          cropType: cropTypeParam,
          seedWeight: 0,
          seedCount: count,
          seedUnit: 'seeds',
          ...sizeFields,
        });
      }
    }

    await fetchLayout();

  } catch (err) {

    console.error('Seed error', err);
    alert(err?.response?.data?.error || 'Seeding failed');

  }

  setMode('idle');
};

  const handleSplitToHalf = async () => {
    if (!menuTray || menuTray.bench === 'germinationChamber') return;
    if (!window.confirm('Split this tray into two half-trays? This tray becomes half A; half B will be free to seed a second half-tray.')) {
      return;
    }
    try {
      await axiosClient.post('/bench-layout/split-to-half', {
        bench: menuTray.bench, layer: menuTray.layer, tray: menuTray.tray,
      });
      await fetchLayout();
    } catch (err) {
      console.error('Split to half failed', err);
      alert(err?.response?.data?.error || 'Split failed');
    }
    setMenuTray(null);
  };

  const handleTransplant = async () => {
    if (!selectedStarter || transplantTargets.length === 0) return;
    try {
      await axiosClient.post('/bench-layout/transplant', { from: selectedStarter, toLocations: transplantTargets });
      await fetchLayout();
    } catch (err) { console.error('Transplant error', err); }
    setMode('idle'); setSelectedStarter(null); setTransplantTargets([]);
  };

const onMove = async ({ from, to, swap = false }) => {
  try {
    const payload = {
      from: from.bench === 'germinationChamber'
        ? { bench: 'germinationChamber', trayId: from.trayId }
        : { bench: from.bench, layer: from.layer, tray: from.tray, ...(from.half != null ? { half: from.half } : {}) },
      to: to.bench === 'germinationChamber'
        ? { bench: 'germinationChamber' }
        : { bench: to.bench, layer: to.layer, tray: to.tray, ...(to.half != null ? { half: to.half } : {}) },
      swap,  // ← new field
    };

    await axiosClient.post('/bench-layout/move', payload);
    await fetchLayout();
    setMode('idle');
  } catch (err) {
    console.error('Move tray failed:', err);
    alert(err?.response?.data?.error || 'Move failed');
  }
};

const handleOpenGerminationSeed = useCallback(() => {
  // Directly open the seed popup for the chamber
  setSeedPopup({
    bench: 'germinationChamber',
  });

  // Reset fields for a fresh entry
  setSelectedCrop('');
  setSeedWeight('');
  setSeedCategory('all');
  setSeedSize('full');
  setSeedQty('1');
}, []);

useEffect(() => {
  if (!selectedCrop || !seedInventory[selectedCrop]) return;

  // Always assume 'weight' mode and 'g' unit per your request
  setSeedMode('weight');
  const suggestedGrams = seedInventory[selectedCrop].gramsPerTray || 0;
  // Half-size trays take half the seed
  setSeedWeight(String(seedSize === 'half' ? suggestedGrams / 2 : suggestedGrams));
}, [selectedCrop, seedInventory, seedSize]);

  // Occupied tray count for summary tab badge
  const occupiedCount = useMemo(() => {
    if (!layout?.benches) return 0;
    let n = 0;
    Object.values(layout.benches).forEach((bench) =>
      bench.layers.forEach((layer) => layer.forEach((slot) => { n += slotTrays(slot).length; }))
    );
    //Now, to include germination chamber trays:
    if (layout.germinationChamber?.trays) {
      n += layout.germinationChamber.trays.length;
    } 
    return n;
  }, [layout]);

  const BENCH_NAMES = ['bench1','bench2','bench3','bench4','bench5','bench6','bench7'];
  const trayProps = { mode, selectedStarter, transplantTargets, handleTrayClick, onMove };

  const chooseLayoutView = (v) => {
    setLayoutView(v);
    try { localStorage.setItem('layout.view', v); } catch { /* private mode */ }
  };

  const layoutToggle = (
    <div className="button-group" role="group" aria-label="Layout view">
      {[['room', 'Room'], ['grid', 'Grid']].map(([v, label]) => (
        <button key={v} type="button" className={layoutView === v ? 'active' : ''} onClick={() => chooseLayoutView(v)}>
          {label}
        </button>
      ))}
    </div>
  );

  const TRAY_TABS = [
    { key: 'layout',  label: 'Layout' },
    { key: 'summary', label: 'All trays', count: occupiedCount },
    { key: 'arrange', label: 'Arrange' },
    { key: 'seeds',   label: 'Seeds' },
  ];

  return (
    <div className="tray-manager-root">
      <PageHeader
        title="Trays"
        actions={activeTab === 'layout' ? layoutToggle : null}
        tabs={TRAY_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {mode === 'transplanting' && (
          <div className="ui-banner ui-banner--warn">
            🌿 Transplant mode: click empty trays to select targets
          </div>
        )}
      </PageHeader>

      {/* Content area */}
      <div className="tray-manager-content">
        {/* ── LAYOUT TAB ── */}
        {activeTab === 'layout' && layout && layoutView === 'room' && (
          <>
            {layout.germinationChamber && (
              <GerminationChamber
                compact
                chamberTrays={layout.germinationChamber.trays}
                onTrayClick={handleTrayClick}
                onMove={onMove}
                mode={mode}
                selectedStarter={selectedStarter}
                transplantTargets={transplantTargets}
                handleOpenGerminationSeed={handleOpenGerminationSeed}
              />
            )}
            <RoomView layout={layout} trayProps={trayProps} />
          </>
        )}

        {activeTab === 'layout' && layout && layoutView === 'grid' && (
          <>
          <div className="bench-grid">
            {BENCH_NAMES.map((benchName, iBench) => {
              const bench = layout.benches[benchName];
              if (!bench) return null;
              const empty = isBenchEmpty(bench);
              const expanded = !!expandedBenches[benchName];

              if (empty && !expanded) {
                return (
                  <div key={benchName} style={stagger(iBench, 'td-fade-up', 20, 60, 360)}>
                    <CollapsedBenchCard
                      benchName={benchName} bench={bench} mode={mode}
                      onExpand={() => setBenchExpanded(benchName, true)}
                      onSeedFirst={(b, layer, tray) => {
                        setSeedPopup({ bench: b, layer, tray });
                        setSelectedCrop(''); setSeedWeight(''); setSeedCategory('all'); setSeedSize('full');
                      }}
                      onMove={onMove}
                    />
                  </div>
                );
              }

              const occupiedInBench = bench.layers.reduce((n, l) => n + l.filter(t => isSlotOccupied(t)).length, 0);
              const totalInBench    = bench.layers.reduce((n, l) => n + l.length, 0);
              return (
                <div key={benchName} style={{
                  background: 'var(--panel, #fff)',
                  borderRadius: 'var(--r-xl)', padding: 18,
                  boxShadow: 'var(--shadow-card)',
                  border: '1px solid var(--border, #d4e0d4)',
                  ...stagger(iBench, 'td-fade-up', 20, 50, 300),
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0, textAlign: 'left' }}>
                        {formatBenchName(benchName)}
                      </h3>
                      <span style={{
                        fontSize: 12, fontWeight: 600,
                        color: occupiedInBench > 0 ? 'var(--brand-2, #2e7d32)' : 'var(--muted, #5e7265)',
                        background: 'var(--panel-2, #eef2ee)',
                        border: '1px solid var(--border, #d4e0d4)',
                        padding: '2px 8px', borderRadius: 999,
                      }}>{occupiedInBench}/{totalInBench}</span>
                    </div>
                    {empty && (
                      <button
                        onClick={() => setBenchExpanded(benchName, false)}
                        style={{
                          background: 'var(--panel-2)', color: 'var(--text)', border: '1px solid var(--border)',
                          padding: '4px 10px', borderRadius: 'var(--r-md)', fontWeight: 700, fontSize: 12, margin: 0, boxShadow: 'none',
                        }}
                      >Collapse</button>
                    )}
                  </div>

                  <div style={{ paddingBottom: 4 }}>
                    {[...bench.layers].reverse().map((layer, layerIndexReversed) => {
                      const layerIndex = bench.layers.length - 1 - layerIndexReversed;
                      return (
                        <div key={layerIndex} className="bench-layer" style={{ marginBottom: 6 }}>
                          {layer.map((slot, trayIndex) => (
                            <SlotCell
                              key={trayIndex} benchName={benchName} layerIndex={layerIndex}
                              trayIndex={trayIndex} slot={slot} trayProps={trayProps}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
                        {layout.germinationChamber && (
              <GerminationChamber
              chamberTrays={layout.germinationChamber.trays}
              onTrayClick={handleTrayClick}
              onMove={onMove}
              mode={mode}
              selectedStarter={selectedStarter}
              transplantTargets={transplantTargets}
              handleOpenGerminationSeed={handleOpenGerminationSeed}
              />
            )}
          </div>
          </>
        )}

        {/* ── SUMMARY TAB ── */}
        {activeTab === 'summary' && (
          <SummaryView
            layout={layout}
            onTrayClick={(bench, layer, tray, half) => {
              // Open menu directly
              if (!layout) return;
              const slot = layout.benches[bench].layers[layer][tray];
              const trayData = isSplitSlot(slot)
                ? (half != null ? slot.halves?.[half] : null)
                : slot;
              setMenuTray({
                bench, layer, tray,
                ...(half != null ? { half } : {}),
                weight: '', trayData: trayData || null,
              });
            }}
            seedInventory={seedInventory}
          />
        )}
        {activeTab === 'arrange' && (
          <ArrangeView onApplied={fetchLayout} />
        )}
        {activeTab === 'seeds' && (
          <SeedInventoryView seeds={seedInventory} loading={seedsLoading} error={seedsError} addCrop={addCrop} updateCrop={updateCrop} removeCrop={removeCrop} reload={reloadSeeds} />
        )}
      </div>

      {/* ── Transplant confirm button (fixed) ── */}
      {awaitingTransplantConfirm && (
        <button
          aria-live="polite"
          disabled={transplantTargets.length === 0}
          style={{
            position: 'fixed', bottom: 30, right: 30,
            padding: '14px 22px', fontSize: 15, borderRadius: 'var(--r-md)',
            background: '#2a8f60', color: '#fff', border: 'none',
            boxShadow: 'var(--shadow)', zIndex: 1000,
          }}
          onClick={() => { handleTransplant(); setAwaitingTransplantConfirm(false); }}
        >
          ✓ Confirm Transplant ({transplantTargets.length} target{transplantTargets.length !== 1 ? 's' : ''})
        </button>
      )}

      {/* ── Tray action modal ── */}
      {menuTray && (
        <ModalShell
          onClose={() => { setMenuTray(null); setAwaitingTransplantConfirm(false); setAppendMode(false); }}
          dark width="min(90vw, 520px)" zIndex={1000} ariaLabel={menuTray.trayData?.cropType || 'Tray'}
          backdropStyle={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
          panelStyle={{ padding: 24, ...stagger(0, 'td-scale-in', 0, 0, 180) }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 17 }}>{menuTray.trayData?.cropType || 'Tray'}</div>
              <div style={{ fontSize: 12, opacity: .6 }}>
                {menuTray.bench === 'germinationChamber'
                  ? `Germination Chamber${menuTray.trayData?.chamberLocation ? ` · 📍 ${menuTray.trayData.chamberLocation}` : ''}`
                  : `L${menuTray.layer + 1} T${menuTray.tray + 1}${menuTray.half != null ? `·${halfLabel(menuTray.half)} (½ tray)` : ''} · ${formatBenchName(menuTray.bench)}`}
              </div>
            </div>
            {menuTray.trayData?.stage && <StagePill stage={menuTray.trayData.stage} />}
          </div>

          {/* Notes editor */}
          <div style={{ paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>Notes</div>
            <textarea
              value={menuTray.trayData?.notes ?? ''}
              onChange={(e) => {
                const td = { ...(menuTray.trayData || {}), notes: e.target.value };
                setMenuTray({ ...menuTray, trayData: td });
              }}
              rows={3}
              style={{
                width: '100%', resize: 'vertical', borderRadius: 'var(--r-md)',
                border: '1px solid rgba(255,255,255,0.2)', padding: '8px 10px',
                color: '#111', fontSize: 13, background: '#fff',
              }}
              placeholder="Write a note…"
            />
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button
                style={{ margin: 0, padding: '7px 14px', fontSize: 13 }}
                onClick={async () => {
                  try {
                    await axiosClient.post('/bench-layout/notes', {
                      bench: menuTray.bench, layer: menuTray.layer, tray: menuTray.tray,
                      trayId: menuTray.trayId, half: menuTray.half,
                      note: menuTray.trayData?.notes || '', mode: 'set',
                    });
                    await fetchLayout();
                  } catch (e) { console.error('Save notes failed', e); }
                }}
              >Save Notes</button>
              <button
                style={{ margin: 0, padding: '7px 14px', fontSize: 13, background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                onClick={() => { setAppendMode(true); setAppendText(''); }}
              >Append…</button>
            </div>

            {appendMode && (
              <div style={{ marginTop: 10 }}>
                <textarea
                  value={appendText}
                  onChange={(e) => setAppendText(e.target.value)}
                  rows={3} placeholder="Append text…"
                  style={{
                    width: '100%', resize: 'vertical', borderRadius: 'var(--r-md)',
                    border: '1px solid rgba(255,255,255,0.2)', padding: '8px 10px', color: '#111', background: '#fff',
                  }}
                />
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <button
                    style={{ margin: 0, padding: '7px 14px', fontSize: 13 }}
                    onClick={async () => {
                      if (!appendText.trim()) return;
                      try {
                        await axiosClient.post('/bench-layout/notes', {
                          bench: menuTray.bench, layer: menuTray.layer, tray: menuTray.tray,
                          trayId: menuTray.trayId, half: menuTray.half,
                          note: appendText, mode: 'append',
                        });
                        await fetchLayout(); setAppendMode(false); setAppendText('');
                      } catch (e) { console.error('Append notes failed', e); }
                    }}
                  >Confirm Append</button>
                  <button
                    style={{ margin: 0, padding: '7px 14px', fontSize: 13, background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                    onClick={() => { setAppendMode(false); setAppendText(''); }}
                  >Cancel</button>
                </div>
              </div>
            )}

            {!!(menuTray.trayData?.legacyNotes?.length) && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>Legacy notes</div>
                <div style={{ maxHeight: 110, overflowY: 'auto', background: '#152028', padding: 10, borderRadius: 'var(--r-md)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  {menuTray.trayData.legacyNotes.map((ln, idx) => (
                    <div key={idx} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, opacity: .7 }}>
                        {new Date(ln.at).toLocaleString()} · {ln.from ? `L${ln.from.layer+1} T${ln.from.tray+1} @ ${ln.from.bench}` : 'unknown'}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{ln.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Weight + actions */}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            <input
              type="number" placeholder="Weight for harvest (grams)"
              value={menuTray.weight || ''}
              onChange={(e) => setMenuTray({ ...menuTray, weight: parseFloat(e.target.value) || '' })}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)',
                border: '1px solid rgba(255,255,255,0.2)', color: '#111',
                background: '#fff', fontSize: 13, marginBottom: 10,
              }}
            />
            <input
              type="number" placeholder="% of tray taken (partial harvest only)"
              min="1" max="100"
              value={menuTray.percentTaken || ''}
              onChange={(e) => setMenuTray({ ...menuTray, percentTaken: parseFloat(e.target.value) || '' })}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)',
                border: '1px solid rgba(255,255,255,0.2)', color: '#111',
                background: '#fff', fontSize: 13, marginBottom: 10,
              }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                style={{ margin: 0, padding: '8px 14px', fontSize: 13 }}
                disabled={!menuTray.weight || isNaN(menuTray.weight) || menuTray.weight <= 0}
                onClick={async () => {
                  await axiosClient.post('/bench-layout/harvest', { ...menuTray, weight: menuTray.weight });
                  await fetchLayout(); setMenuTray(null);
                }}
              >Harvest</button>
              <button
                style={{ margin: 0, padding: '8px 14px', fontSize: 13, background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                disabled={!menuTray.weight || isNaN(menuTray.weight) || menuTray.weight <= 0}
                onClick={async () => {
                  await axiosClient.post('/bench-layout/partial-harvest', { ...menuTray, weight: menuTray.weight, percentTaken: menuTray.percentTaken || undefined });
                  await fetchLayout(); setMenuTray(null);
                }}
              >Partial Harvest</button>
              <button
                style={{ margin: 0, padding: '8px 14px', fontSize: 13, background: '#2a8f60', color: '#fff', border: 'none' }}
                onClick={() => {
                  setSelectedStarter(menuTray);
                  setMode('transplanting');
                  setAwaitingTransplantConfirm(true);
                  setTransplantTargets([]);
                  setMenuTray(null);
                  setActiveTab('layout');
                }}
              >Transplant</button>
              {menuTray.bench !== 'germinationChamber' && menuTray.half == null && menuTray.trayData?.size !== 'half' && (
                <button
                  style={{ margin: 0, padding: '8px 14px', fontSize: 13, background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                  onClick={handleSplitToHalf}
                  title="Forgot to seed this as a half tray? Split it into two halves."
                >Split into Halves</button>
              )}
              <button
                style={{ margin: 0, padding: '8px 14px', fontSize: 13, background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
                onClick={() => { setMenuTray(null); setAwaitingTransplantConfirm(false); }}
              >Cancel</button>
            </div>
          </div>
        </ModalShell>
      )}

      {/* ── Seed modal ── */}
      {seedPopup && (
        <ModalShell
          onClose={() => setSeedPopup(null)}
          dark width="min(90vw, 400px)" zIndex={1100} ariaLabel="Seed Tray"
          backdropStyle={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
          panelStyle={{ padding: 24, ...stagger(0, 'td-scale-in', 0, 0, 180) }}
        >
          <h4 style={{ marginTop: 0, marginBottom: 14, fontWeight: 800 }}>
            🌱 Seed Tray
            {seedPopup.half != null && (
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700, color: '#a5d6a7' }}>
                Half {halfLabel(seedPopup.half)}
              </span>
            )}
          </h4>

          {/* Tray size - locked to half when a specific half-cell was clicked */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)' }}>Size:</span>
            {[['full', 'Full tray'], ['half', '½ Micro tray']].map(([v, l]) => (
              <button
                key={v}
                disabled={seedPopup.half != null && v === 'full'}
                onClick={() => setSeedSize(v)}
                style={{
                  margin: 0, padding: '5px 12px', borderRadius: 'var(--r-md)', fontSize: 13,
                  background: seedSize === v ? '#2a8f60' : 'rgba(255,255,255,0.1)',
                  color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
                  opacity: seedPopup.half != null && v === 'full' ? 0.4 : 1,
                }}
              >{l}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {['all', 'food', 'flower'].map((cat) => (
              <button
                key={cat}
                onClick={() => setSeedCategory(cat)}
                style={{
                  margin: 0, padding: '5px 12px', borderRadius: 'var(--r-md)', fontSize: 13,
                  background: seedCategory === cat ? 'var(--brand-2, #2e7d32)' : 'rgba(255,255,255,0.1)',
                  color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
                }}
              >{cat.charAt(0).toUpperCase() + cat.slice(1)}</button>
            ))}
          </div>

          <select
            value={selectedCrop}
            onChange={(e) => setSelectedCrop(e.target.value)}
            style={{
              width: '100%', padding: '9px 10px', borderRadius: 'var(--r-md)', marginBottom: 12,
              background: '#fff', color: '#111', border: '1px solid rgba(255,255,255,0.3)', fontSize: 13,
            }}
          >
            <option value="" disabled>Select crop…</option>
            {cropOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>

          {seedMode === 'weight' ? (
            <>
              <input
                type="number" placeholder="Seed weight (grams)"
                value={seedWeight} onChange={(e) => setSeedWeight(e.target.value)}
                style={{ width: '100%', padding: '9px 10px', borderRadius: 'var(--r-md)', marginBottom: 6, color: '#111', background: '#fff', border: '1px solid rgba(255,255,255,0.2)', fontSize: 13 }}
              />
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
                Suggested: {selectedCrop
                  ? (seedSize === 'half'
                      ? (seedInventory[selectedCrop]?.gramsPerTray || 0) / 2
                      : (seedInventory[selectedCrop]?.gramsPerTray || 0))
                  : 0}g
                {seedSize === 'half' ? ' (½ tray)' : ''}
                · Type: Food
              </div>
            </>
          ) : (
            <>
              <input
                type="number" placeholder="Seeds per tray"
                value={seedCount} onChange={(e) => setSeedCount(e.target.value)}
                style={{ width: '100%', padding: '9px 10px', borderRadius: 'var(--r-md)', marginBottom: 6, color: '#111', background: '#fff', border: '1px solid rgba(255,255,255,0.2)', fontSize: 13 }}
              />
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
                Suggested: {selectedCrop ? seedsPerTrayFor(selectedCrop) : 0} seeds
                · Type: {selectedCrop ? categoryFor(selectedCrop) : '-'}
              </div>
            </>
          )}

          {seedPopup.bench === 'germinationChamber' && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)' }}>Number of trays:</span>
              <input
                type="number" min="1" max="50" step="1"
                value={seedQty} onChange={(e) => setSeedQty(e.target.value)}
                style={{ width: 70, padding: '7px 8px', borderRadius: 'var(--r-md)', color: '#111', background: '#fff', border: '1px solid rgba(255,255,255,0.2)', fontSize: 13 }}
              />
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ margin: 0, flex: 1, padding: '9px', fontSize: 14 }}
              disabled={
                !selectedCrop ||
                (seedPopup.bench === 'germinationChamber' &&
                  !(Number(seedQty) >= 1 && Number(seedQty) <= 50 && Number.isInteger(Number(seedQty)))) ||
                (seedMode === 'weight'
                  ? isNaN(parseFloat(seedWeight)) || parseFloat(seedWeight) <= 0
                  : isNaN(parseInt(seedCount, 10)) || parseInt(seedCount, 10) <= 0)
              }
              onClick={() => {
                handleSeed(seedPopup.bench, seedPopup.layer, seedPopup.tray, selectedCrop, seedPopup.half);
                setSeedPopup(null);
              }}
            >Confirm</button>
            <button
              style={{ margin: 0, padding: '9px 14px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', fontSize: 14 }}
              onClick={() => setSeedPopup(null)}
            >Cancel</button>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default TrayManager;